/**
 * SEATMAP.VUE - NAVIGATION GUIDE
 * ================================
 * This is the main seatmap editor component. Navigate using these sections:
 *
 * 1. IMPORTS & CONSTANTS (line 156-185) - Vue imports, components, constants
 * 2. STATE & REFS (line 187-239) - Core reactive state variables
 * 3. COMPUTED PROPERTIES (line 242-320) - Derived state and UI logic
 * 4. UTILITY FUNCTIONS (line 322-425) - Helper functions for data manipulation
 * 5. EDIT MODE & INTERACTION (line 427-535) - Mode switching and canvas interaction
 * 6. HISTORY & UNDO (line 537-650) - Undo/redo system
 * 7. ROW MANAGEMENT (line 652-850) - Add, delete, modify rows and seats
 * 8. LABEL MANAGEMENT (line 852-980) - Add, update, delete labels
 * 9. SELECTION & DELETION (line 982-1200) - Seat/wall/row/label selection and deletion
 * 10. KEYBOARD & CLIPBOARD (line 1202-1320) - Copy/paste and keyboard shortcuts
 * 11. IMPORT/EXPORT (line 1322-1650) - File I/O and data conversion
 * 12. LAYOUT & RENDERING (line 1652-2050) - Seat positioning and viewport culling
 * 13. DRAG & DROP (line 2052-2350) - Row and seat dragging
 * 14. ROW LABELS & INTERACTIONS (line 2352-2500) - Row label click/drag handlers
 * 15. EXPORT & LIFECYCLE (line 2502-2650) - Export function and component lifecycle
 */
 <template>
	<Teleport to="body">
	<main class="editor-page">
		<section class="canvas-shell">
			<SeatmapToolbar
				:rows-count="rows.length"
				:walls-count="walls.length"
				:shapes-count="decorativeShapes.length"
				:selection-count="selectionCount"
				:toolbar-note="toolbarNote"
				:selection-action-label="disableSelectionButtonLabel"
				:can-undo="canUndo"
				:edit-mode="editMode"
				:seat-edit-mode="seatEditMode"
				:pending-wall-start="pendingWallStart"
				:show-grid="showGrid"
				:snap-to-grid="snapToGrid"
				:has-selection="hasSelection"
				:saving="props.saving"
				@open-guide="openGuide"
				@undo="undo"
				@set-edit-mode="setEditMode"
				@set-seat-edit-mode="setSeatEditMode"
				@cancel-pending-wall="cancelPendingWall"
				@toggle-grid="toggleGridVisibility"
				@toggle-snap-to-grid="snapToGrid = !snapToGrid"
				@add-row="addRow"
				@add-label="addLabel"
				@add-shape="addDecorativeShape"
				@trigger-import="triggerImport"
				@export-seatmap="exportSeatmap"
				@toggle-disable-selection="toggleDisableSelection"
				@delete-selection="deleteSelection"
				@save-seatmap="handleSave"
				@close-editor="handleClose"
			/>

			<SeatmapGuideOverlay
				:is-open="showGuide"
				:show-on-startup="showGuideOnStartup"
				@close="closeGuide"
				@update:show-on-startup="showGuideOnStartup = $event"
			/>

			<ZoneManagerModal
				:is-open="showZoneManager"
				:zones="seatmapZones"
				:busy="zonesBusy"
				@close="showZoneManager = false"
				@add="addSeatmapZone"
				@rename="renameSeatmapZone"
				@delete="deleteSeatmapZone"
			/>

			<input
				ref="importInputRef"
				type="file"
				accept="application/json,.json"
				style="display: none"
				@change="onImportFileChange"
			/>

			<SeatmapCanvas
				ref="gridRef"
				:grid-size="gridSize"
				:grid-size-x="canvasGridPitch.x"
				:grid-size-y="canvasGridPitch.y"
				:grid-offset-x="canvasGridPhase.x"
				:grid-offset-y="canvasGridPhase.y"
				:visible-rows="visibleRows"
				:walls="walls"
				:selected-wall-ids="Array.from(selectedWallIds)"
				:pending-wall-start="pendingWallStart"
				:pointer-down-point="wallPointerDownPoint"
				:pointer-move-point="wallPointerMovePoint"
				:pointer-up-point="wallPointerUpPoint"
				:labels="labels"
				:selected-label-id="selectedLabelId"
				:decorative-shapes="decorativeShapes"
				:selected-shape-id="selectedShapeId"
				:selected-row-action-style="selectedRowActionStyle"
				:selected-seat-action-style="selectedSeatActionStyle"
				:edit-mode="editMode"
				:seat-edit-mode="seatEditMode"
				:snap-to-grid="snapToGrid"
				:show-grid="showGrid"
				:is-seat-selected="isSeatSelected"
				:is-row-selected="isRowSelected"
				:is-row-dragging="isRowDragging"
				:get-seat-config="getSeatConfig"
				:get-seat-rect-config="getSeatRectConfig"
				:get-seat-text-config="getSeatTextConfig"
				:should-show-seat-text="shouldShowSeatText"
				:should-show-selected-seat-delete-button="shouldShowSelectedSeatDeleteButton"
				:get-selected-seat-delete-button-config="getSelectedSeatDeleteButtonConfig"
				:get-delete-seat-button-rect-config="getDeleteSeatButtonRectConfig"
				:get-delete-seat-button-bin-body-config="getDeleteSeatButtonBinBodyConfig"
				:get-delete-seat-button-bin-lid-config="getDeleteSeatButtonBinLidConfig"
				:get-delete-seat-button-bin-handle-config="getDeleteSeatButtonBinHandleConfig"
				:should-show-add-seat-button="shouldShowAddSeatButton"
				:get-add-seat-button-config="getAddSeatButtonConfig"
				:get-add-seat-button-rect-config="getAddSeatButtonRectConfig"
				:get-add-seat-button-text-config="getAddSeatButtonTextConfig"
				:should-show-delete-seat-button="shouldShowDeleteSeatButton"
				:get-delete-seat-button-config="getDeleteSeatButtonConfig"
				:get-row-label-config="getRowLabelConfig"
				@canvas-click="onCanvasClick"
				@canvas-pointerdown="onCanvasPointerDown"
				@canvas-pointermove="onCanvasPointerMove"
				@canvas-pointerup="onCanvasPointerUp"
				@camera-change="onCameraChange"
				@walls-change="onWallsChange"
				@selection-change="onWallSelectionChange"
				@pending-start-change="onPendingWallStartChange"
				@open-row-options="activeSidebar = 'row'"
				@open-seat-options="activeSidebar = 'seat'"
				@seat-click="onSeatClick"
				@seat-dragstart="onSeatDragStart"
				@seat-dragmove="onSeatDragMove"
				@seat-dragend="onSeatDragEnd"
				@selected-seat-delete-pointerdown="onSelectedSeatDeleteButtonPointerDown"
				@selected-seat-delete-click="onSelectedSeatDeleteButtonClick"
				@add-seat-button-pointerdown="onAddSeatButtonPointerDown"
				@add-seat-button-click="onAddSeatButtonClick"
				@delete-seat-button-pointerdown="onDeleteSeatButtonPointerDown"
				@delete-seat-button-click="onDeleteSeatButtonClick"
				@row-label-click="onRowLabelClick"
				@row-label-dragstart="onRowLabelDragStart"
				@row-label-dragmove="onRowLabelDragMove"
				@row-label-dragend="onRowLabelDragEnd"
				@label-select="handleSelectLabel"
				@label-position-changed="handleLabelPositionChanged"
				@shape-select="handleSelectShape"
				@shape-position-changed="handleShapePositionChanged"
				@shape-resize-changed="handleShapeResizeChanged"
			/>

			<OptionsSidebar
				:is-open="!!activeSidebar"
				:mode="activeSidebar"
				:label="selectedLabel"
				:shape="selectedShape"
				:selected-rows="selectedRowsForPanel"
				:selected-seats="selectedSeatsForPanel"
				:zones="seatmapZones"
				@close="activeSidebar = null"
				@update-text="handleUpdateLabelText"
				@update-font-size="handleUpdateLabelFontSize"
				@update-font-family="handleUpdateLabelFontFamily"
				@update-color="handleUpdateLabelColor"
				@duplicate-rows="duplicateSelectedRows"
				@reset-rows="resetSelectedRowsToDefault"
				@update-seats="handleUpdateSelectedRowsSeatCount"
				@update-start-number="handleUpdateSelectedRowsStartNumber"
				@update-reverse-numbering="handleUpdateSelectedRowsReverseNumbering"
				@update-row-label="handleUpdateSelectedRowsLabel"
				@update-row-label-position="handleUpdateSelectedRowsLabelPosition"
				@update-seat-title="handleUpdateSelectedSeatsTitle"
				@update-seat-zone="handleUpdateSelectedSeatsZone"
				@manage-zones="showZoneManager = true"
				@update-rotation="handleUpdateSelectedRowsRotation"
				@update-curvature="handleUpdateSelectedRowsCurvature"
				@update-skew="handleUpdateSelectedRowsSkew"
				@update-shape-width="handleUpdateShapeWidth"
				@update-shape-height="handleUpdateShapeHeight"
				@update-shape-corner-radius="handleUpdateShapeCornerRadius"
				@update-shape-fill="handleUpdateShapeFill"
				@update-shape-border="handleUpdateShapeBorder"
				@update-shape-text="handleUpdateShapeText"
			/>
		</section>
	</main>
	</Teleport>
</template>

<script setup lang="ts">


import { computed, nextTick, onBeforeUnmount, onMounted, ref, toRaw } from 'vue'
import axios from 'axios'
import OptionsSidebar from './OptionsSidebar.vue'
import SeatmapCanvas from './SeatmapCanvas.vue'
import SeatmapGuideOverlay from './SeatmapGuideOverlay.vue'
import SeatmapToolbar from './SeatmapToolbar.vue'
import ZoneManagerModal from './ZoneManagerModal.vue'
import {
	ADD_SEAT_BUTTON_RECT_CONFIG,
	ADD_SEAT_BUTTON_TEXT_CONFIG,
	DEFAULT_GRID_SIZE,
	DEFAULT_ROW_COUNT,
	DEFAULT_SEATS_PER_ROW,
	DEFAULT_SEAT_CORNER_RADIUS,
	DEFAULT_SEAT_SIZE,
	DEFAULT_SHAPE_CORNER_RADIUS,
	DEFAULT_SHAPE_FILL,
	DEFAULT_SHAPE_HEIGHT,
	DEFAULT_SHAPE_WIDTH,
	DELETE_SEAT_BUTTON_BIN_BODY_CONFIG,
	DELETE_SEAT_BUTTON_BIN_HANDLE_CONFIG,
	DELETE_SEAT_BUTTON_BIN_LID_CONFIG,
	DELETE_SEAT_BUTTON_RECT_CONFIG,
	ENABLE_VIEWPORT_CULLING_SEAT_THRESHOLD,
	LARGE_MAP_HISTORY_LIMIT,
	LARGE_MAP_HISTORY_SEAT_THRESHOLD,
	LARGE_SEAT_RENDER_THRESHOLD,
	MEDIUM_MAP_DEBOUNCED_HISTORY_SEAT_THRESHOLD,
	MIN_SHAPE_SIZE,
	SEATMAP_ZONE_TYPE,
	SELECTED_SEAT_DELETE_BUTTON_CONFIG,
	VIEWPORT_CULLING_MARGIN
} from '~/lib/seatmapeditor/constants'
import type {
	ClipboardData,
	DecorativeShape,
	ImportSeat,
	ImportSeatMap,
	ImportShape,
	JohkuSeatmapExport,
	Label,
	Row,
	RowClipboardItem,
	RowDragState,
	RowLabelPosition,
	RowLayoutEntry,
	RowSeatBounds,
	Seat,
	SeatmapZone,
	SeatPosition,
	SeatmapSnapshot,
	VisibleRowEntry,
	VisibleSeatEntry,
	Wall
} from '~/lib/seatmapeditor/types'
import { applyRowSeatNumbering, cloneSerializable, getRowDisplayLabel, getRowLabel, getRowLabelPosition, getSeatDisplayTitle, parseSeatKey, toSeatKey, trimRenderConfigCache } from '~/lib/seatmapeditor/utils'

const { $translate } = useNuxtApp()
const _ = $translate as (key: string) => string

const props = withDefaults(defineProps<{
	initialData?: JohkuSeatmapExport | Record<string, unknown> | null
	saving?: boolean
}>(), {
	initialData: null,
	saving: false
})

const emit = defineEmits<{
	(e: 'save', data: JohkuSeatmapExport): void
	(e: 'close'): void
}>()

// Editable decorative rect/rrect shapes (e.g. a "screen" block), matching what the
// production seat map viewer knows how to render (see SeatMap.vue -> makeRRect).
const decorativeShapes = ref<DecorativeShape[]>([])
// Any other legacy shape kind the editor doesn't understand at all (not even as a plain
// rect/rrect) - kept verbatim and re-emitted untouched so it survives a round trip.
const unsupportedShapes = ref<ImportShape[]>([])
const importedCanvasSize = ref<{ w?: number; h?: number } | null>(null)

const seatSize = DEFAULT_SEAT_SIZE
const gridSize = ref(DEFAULT_GRID_SIZE)
const rows = ref<Row[]>([])
const activeRowIndex = ref(0)
const labels = ref<Label[]>([])
const selectedLabelId = ref<number | null>(null)
const selectedShapeId = ref<string | null>(null)
const activeSidebar = ref<'label' | 'row' | 'shape' | 'seat' | null>(null)
const selectedRowIndices = ref<Set<number>>(new Set())
const lastSelectedRowIndex = ref<number | null>(null)
const selectedSeatIds = ref<Set<string>>(new Set())
const lastSelectedSeatId = ref<string | null>(null)
const walls = ref<Wall[]>([])
const selectedWallIds = ref<Set<string>>(new Set())
const pendingWallStart = ref<{ x: number; y: number } | null>(null)
const editMode = ref<'seats' | 'walls'>('seats')
const seatEditMode = ref<'rows' | 'seats'>('rows')
const hasSelection = computed(() => selectedSeatIds.value.size > 0 || selectedRowIndices.value.size > 0 || selectedWallIds.value.size > 0 || selectedLabelId.value !== null || selectedShapeId.value !== null)
const selectionCount = computed(() => selectedSeatIds.value.size + selectedRowIndices.value.size + selectedWallIds.value.size + (selectedLabelId.value !== null ? 1 : 0) + (selectedShapeId.value !== null ? 1 : 0))
const snapToGrid = ref(true)
const showGrid = ref(true)
const showGuide = ref(false)
const showGuideOnStartup = ref(true)
const seatmapZones = ref<SeatmapZone[]>([])
const showZoneManager = ref(false)
const zonesBusy = ref(false)
const rowDragState = ref<RowDragState | null>(null)
const rowDragPreviewOffset = ref<{ x: number; y: number }>({ x: 0, y: 0 })
const importInputRef = ref<HTMLInputElement | null>(null)
const gridRef = ref<{
	resetZoom?: () => void
	focusWorldBounds?: (bounds: { minX: number; minY: number; maxX: number; maxY: number }, padding?: number) => void
} | null>(null)
const clipboardData = ref<ClipboardData | null>(null)
const pasteStep = ref(1)
const wallPointerDownPoint = ref<{ x: number; y: number } | null>(null)
const wallPointerMovePoint = ref<{ x: number; y: number } | null>(null)
const wallPointerUpPoint = ref<{ x: number; y: number } | null>(null)
const cameraX = ref(0)
const cameraY = ref(0)
const cameraScale = ref(1)
const viewportWidth = ref(0)
const viewportHeight = ref(0)

let rafUpdatePending = false
const isCalculationPaused = ref(false)
const isInteractingWithSlider = ref(false)
let sliderEndTimer: ReturnType<typeof setTimeout> | null = null

const history = ref<SeatmapSnapshot[]>([])
const historyIndex = ref(-1)
const isRestoringHistory = ref(false)
const MAX_HISTORY = 500
const canUndo = computed(() => historyIndex.value > 0)
let wallHistoryCommitTimer: ReturnType<typeof setTimeout> | null = null
let historyCommitTimer: ReturnType<typeof setTimeout> | null = null
let rowPropertyUpdateTimer: ReturnType<typeof setTimeout> | null = null
const seatRectConfigCache = new Map<string, Record<string, unknown>>()
const seatTextConfigCache = new Map<string, Record<string, unknown>>()

const cloneDeep = <T>(value: T): T => {
	return cloneSerializable(toRaw(value) as T)
}

const getTotalSeatCount = () => {
	let total = 0
	for (const row of rows.value) {
		if (Array.isArray(row.seats)) {
			total += row.seats.length
		} else if (Number.isFinite(row.seats)) {
			total += row.seats
		}
	}
	return total
}

const totalSeatCount = computed(() => getTotalSeatCount())
const shouldUseLiteSeatRendering = computed(() => totalSeatCount.value >= LARGE_SEAT_RENDER_THRESHOLD)
const shouldRenderAllSeatText = computed(() => {
	if (totalSeatCount.value < 500) {
		return true
	}

	return cameraScale.value >= 1.15
})
const seatTextFontSize = computed(() => {
	if (totalSeatCount.value >= 1500) {
		return cameraScale.value < 0.8 ? 7 : 8
	}

	if (totalSeatCount.value >= 900) {
		return cameraScale.value < 0.75 ? 8 : 9
	}

	if (totalSeatCount.value >= 500) {
		return cameraScale.value < 0.65 ? 9 : 10
	}

	return cameraScale.value < 0.55 ? 10 : 12
})
const shouldUseViewportCulling = computed(() => totalSeatCount.value >= ENABLE_VIEWPORT_CULLING_SEAT_THRESHOLD)
const viewportWorldBounds = computed(() => {
	if (cameraScale.value <= 0 || viewportWidth.value <= 0 || viewportHeight.value <= 0) {
		return null
	}

	return {
		left: (0 - cameraX.value) / cameraScale.value,
		right: (viewportWidth.value - cameraX.value) / cameraScale.value,
		top: (0 - cameraY.value) / cameraScale.value,
		bottom: (viewportHeight.value - cameraY.value) / cameraScale.value
	}
})

const toolbarNote = computed(() => {
	if (editMode.value === 'walls') {
		if (pendingWallStart.value) {
			return _('Wall draw mode: keep dragging and release to finish the wall.')
		}
		return _('Wall draw mode: press and drag on the canvas to draw walls. Drag endpoints to resize.')
	}

	if (seatEditMode.value === 'seats') {
		return _('Seat mode: click seats to select them. Use Ctrl/Cmd for multi-select and Shift for range-select. Seats with a dashed border are not saved yet - prefer deleting those.')
	}

	return _('Row mode: click a row label or any seat in a row to select it. Drag to move selected rows.')
})

const selectedLabel = computed(() => labels.value.find((label) => label.id === selectedLabelId.value) ?? null)
const selectedShape = computed(() => decorativeShapes.value.find((shape) => shape.id === selectedShapeId.value) ?? null)

const selectedSeatLookup = computed(() => {
	const lookup = new Map<number, Set<number>>()
	for (const seatKey of selectedSeatIds.value) {
		const parsedSeat = parseSeatKey(seatKey)
		if (!parsedSeat) {
			continue
		}

		const rowSet = lookup.get(parsedSeat.rowIndex)
		if (rowSet) {
			rowSet.add(parsedSeat.seatIndex)
			continue
		}

		lookup.set(parsedSeat.rowIndex, new Set([parsedSeat.seatIndex]))
	}

	return lookup
})

const isSeatSelected = (rowIndex: number, seatIndex: number) => {
	return selectedSeatLookup.value.get(rowIndex)?.has(seatIndex) ?? false
}

const selectedRowsForPanel = computed(() => {
	return Array.from(selectedRowIndices.value)
		.sort((a, b) => a - b)
		.map((rowIndex) => {
			const row = rows.value[rowIndex]
			if (!row || !Array.isArray(row.seats)) {
				return null
			}
			return {
			rowIndex,
				row
			}
		})
		.filter((entry): entry is { rowIndex: number; row: Row & { seats: Seat[] } } => !!entry)
})

const selectedSeatsForPanel = computed(() => {
	return Array.from(selectedSeatIds.value)
		.map((seatKey) => {
			const parsed = parseSeatKey(seatKey)
			if (!parsed) {
				return null
			}
			const row = rows.value[parsed.rowIndex]
			if (!row || !Array.isArray(row.seats)) {
				return null
			}
			const seat = row.seats[parsed.seatIndex]
			if (!seat) {
				return null
			}
			return {
				rowIndex: parsed.rowIndex,
				seatIndex: parsed.seatIndex,
				seat
			}
		})
		.filter((entry): entry is { rowIndex: number; seatIndex: number; seat: Seat } => !!entry)
})

const normalizeRows = (inputRows: Row[]) => {
	return inputRows.map((row, rowIndex) => {
		const normalizedRotation = Number.isFinite(row.rotation) ? Number(row.rotation) : 0
		const normalizedCurvature = Number.isFinite(row.curvature) ? Number(row.curvature) : 0
		const normalizedSkew = Number.isFinite(row.skew) ? Number(row.skew) : 0
		const hasExplicitStart = row.startNumber !== undefined && row.startNumber !== null
		const hasExplicitReverse = row.reverseNumbering !== undefined && row.reverseNumbering !== null
		const normalizedStartNumber = hasExplicitStart ? Math.max(1, Math.round(Number(row.startNumber) || 1)) : undefined
		const normalizedReverseNumbering = hasExplicitReverse ? !!row.reverseNumbering : undefined
		const normalizedLabel = row.label != null ? String(row.label) : undefined
		const normalizedLabelPosition = row.labelPosition === 'end' ? 'end' : (row.labelPosition === 'start' ? 'start' : undefined)

		if (Array.isArray(row.seats)) {
			const normalizedRow: Row = {
				...row,
				label: normalizedLabel,
				rotation: normalizedRotation,
				curvature: normalizedCurvature,
				skew: normalizedSkew,
				startNumber: normalizedStartNumber,
				reverseNumbering: normalizedReverseNumbering,
				labelPosition: normalizedLabelPosition,
				seats: row.seats.map((seat, seatIndex) => ({
					...seat,
					index: seat.index ?? seatIndex + 1,
					title: seat.title != null && String(seat.title).trim() !== '' ? String(seat.title) : undefined,
					x: seat.x ?? defaultSeatX(seatIndex),
					y: seat.y ?? defaultSeatY(rowIndex),
					zone: seat.zone,
					bg: seat.bg,
					linkedSeats: seat.linkedSeats,
					w: seat.w,
					h: seat.h,
					r: seat.r
				}))
			}
			applyRowSeatNumbering(normalizedRow)
			return normalizedRow
		}

		const seatCount = Math.max(0, Number(row.seats) || 0)
		const normalizedRow: Row = {
			...row,
			label: normalizedLabel ?? getRowLabel(rowIndex),
			rotation: normalizedRotation,
			curvature: normalizedCurvature,
			skew: normalizedSkew,
			startNumber: normalizedStartNumber ?? 1,
			reverseNumbering: normalizedReverseNumbering ?? false,
			labelPosition: normalizedLabelPosition ?? 'start',
			seats: Array.from({ length: seatCount }, (_, seatIndex) => ({
				index: seatIndex + 1,
				x: defaultSeatX(seatIndex),
				y: defaultSeatY(rowIndex)
			}))
		}
		applyRowSeatNumbering(normalizedRow)
		return normalizedRow
	})
}

const relabelRowsNumerically = () => {
	rows.value.forEach((row, index) => {
		if (!row.label || String(row.label).trim() === '') {
			row.label = getRowLabel(index)
		}
	})
}

const positiveMod = (value: number, modulus: number) => {
	return ((value % modulus) + modulus) % modulus
}

// Picks the most frequently occurring delta (rounded to the nearest pixel) out of a
// list of consecutive-seat distances. Using the mode instead of just the first pair
// makes pitch detection resilient to the odd irregular gap (missing seat, aisle, etc).
const computeModePitch = (deltas: number[]): number | null => {
	const counts = new Map<number, number>()
	for (const delta of deltas) {
		const rounded = Math.round(delta)
		if (rounded <= 0) {
			continue
		}
		counts.set(rounded, (counts.get(rounded) ?? 0) + 1)
	}

	let bestValue: number | null = null
	let bestCount = 0
	for (const [value, count] of counts) {
		if (count > bestCount) {
			bestCount = count
			bestValue = value
		}
	}
	return bestValue
}

// Real/imported seat maps don't necessarily use the editor's own default 44px pitch -
// production maps can space seats/rows differently. Detecting the map's actual
// horizontal (seat-to-seat) and vertical (row-to-row) spacing lets the background
// grid, snapping, and new seat placement all line up with the loaded data instead of
// drifting further from the grid with every seat/row away from the first one.
const canvasGridPitch = computed(() => {
	const xDeltas: number[] = []
	const rowReferenceYs: number[] = []

	for (const row of rows.value) {
		const seats = Array.isArray(row.seats) ? row.seats : []
		const finiteXSeats = seats
			.filter((seat) => Number.isFinite(seat.x))
			.slice()
			.sort((a, b) => (a.x as number) - (b.x as number))
		for (let i = 1; i < finiteXSeats.length; i++) {
			xDeltas.push((finiteXSeats[i]!.x as number) - (finiteXSeats[i - 1]!.x as number))
		}

		const firstSeatWithY = seats.find((seat) => Number.isFinite(seat.y))
		if (firstSeatWithY) {
			rowReferenceYs.push(firstSeatWithY.y as number)
		}
	}

	const yDeltas: number[] = []
	for (let i = 1; i < rowReferenceYs.length; i++) {
		yDeltas.push(rowReferenceYs[i]! - rowReferenceYs[i - 1]!)
	}

	return {
		x: computeModePitch(xDeltas) ?? gridSize.value,
		y: computeModePitch(yDeltas) ?? gridSize.value
	}
})

// Real/imported seat maps are rarely laid out starting at world (0, 0) - a row can
// start at an arbitrary x/y (e.g. x: 270) while still using the standard pitch. To
// keep the background grid (and new rows/seats) visually consistent with whatever
// map is currently loaded, derive the grid's "phase" from the first seat we find
// instead of always assuming an origin of (0, 0).
const canvasGridPhase = computed(() => {
	for (const row of rows.value) {
		const seats = Array.isArray(row.seats) ? row.seats : []
		for (const seat of seats) {
			if (Number.isFinite(seat.x) && Number.isFinite(seat.y)) {
				return {
					x: positiveMod(seat.x as number, canvasGridPitch.value.x),
					y: positiveMod(seat.y as number, canvasGridPitch.value.y)
				}
			}
		}
	}
	return { x: 0, y: 0 }
})

const defaultSeatX = (seatIdx: number) => {
	return seatIdx * canvasGridPitch.value.x + canvasGridPhase.value.x
}

const defaultSeatY = (rowIdx: number) => {
	return rowIdx * canvasGridPitch.value.y + canvasGridPhase.value.y
}

const getSeatMargin = (seat?: Seat) => {
	const seatWidth = seat?.w ?? seatSize
	const seatHeight = seat?.h ?? seatSize
	return {
		x: Math.max(0, (canvasGridPitch.value.x - seatWidth) / 2),
		y: Math.max(0, (canvasGridPitch.value.y - seatHeight) / 2)
	}
}

const guideStorageKey = 'seatmap.toolbar-guide-seen'

const openGuide = () => {
	showGuide.value = true
}

const toggleGridVisibility = () => {
	showGrid.value = !showGrid.value
	if (!showGrid.value) {
		snapToGrid.value = false
	}
}

const setEditMode = (mode: 'seats' | 'walls') => {
	editMode.value = mode
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	pendingWallStart.value = null
	wallPointerDownPoint.value = null
	wallPointerMovePoint.value = null
	wallPointerUpPoint.value = null
	rowDragState.value = null
	rowDragPreviewOffset.value = { x: 0, y: 0 }
	if (mode === 'walls') {
		selectedRowIndices.value = new Set()
		selectedLabelId.value = null
		selectedShapeId.value = null
		activeSidebar.value = null
		lastSelectedRowIndex.value = null
	} else {
		seatEditMode.value = 'rows'
		selectedWallIds.value = new Set()
		if (rows.value.length > 0) {
			const clampedActiveRow = Math.max(0, Math.min(activeRowIndex.value, rows.value.length - 1))
			activeRowIndex.value = clampedActiveRow
			selectedRowIndices.value = new Set([clampedActiveRow])
			lastSelectedRowIndex.value = clampedActiveRow
		} else {
			selectedRowIndices.value = new Set()
			lastSelectedRowIndex.value = null
		}
	}
}

const setSeatEditMode = (mode: 'rows' | 'seats') => {
	if (editMode.value !== 'seats') {
		return
	}

	seatEditMode.value = mode
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	selectedRowIndices.value = new Set()
	lastSelectedRowIndex.value = null
	rowDragState.value = null
	rowDragPreviewOffset.value = { x: 0, y: 0 }
	if (mode !== 'rows' && activeSidebar.value === 'row') {
		activeSidebar.value = null
	}
	if (mode !== 'seats' && activeSidebar.value === 'seat') {
		activeSidebar.value = null
	}

	if (mode === 'rows' && rows.value.length > 0) {
		const clampedActiveRow = Math.max(0, Math.min(activeRowIndex.value, rows.value.length - 1))
		activeRowIndex.value = clampedActiveRow
		selectedRowIndices.value = new Set([clampedActiveRow])
		lastSelectedRowIndex.value = clampedActiveRow
	}
}

const cancelPendingWall = () => {
	pendingWallStart.value = null
}

const onCanvasClick = (point: { x: number; y: number }) => {
	if (editMode.value !== 'walls') {
		selectedSeatIds.value = new Set()
		lastSelectedSeatId.value = null
		selectedWallIds.value = new Set()
		selectedLabelId.value = null
		selectedShapeId.value = null
		activeSidebar.value = null
		lastSelectedRowIndex.value = null
		pendingWallStart.value = null
		wallPointerDownPoint.value = null
		wallPointerMovePoint.value = null
		wallPointerUpPoint.value = null
		return
	}

	// Click handling is retained only for deselection/background behavior.
	void point
}

const onCanvasPointerDown = (point: { x: number; y: number }) => {
	if (editMode.value !== 'walls') {
		return
	}
	wallPointerDownPoint.value = { x: point.x, y: point.y }
}

const onCanvasPointerMove = (point: { x: number; y: number }) => {
	if (editMode.value !== 'walls') {
		return
	}
	wallPointerMovePoint.value = { x: point.x, y: point.y }
}

const onCanvasPointerUp = (point: { x: number; y: number }) => {
	if (editMode.value !== 'walls') {
		return
	}
	wallPointerUpPoint.value = { x: point.x, y: point.y }
}

const onCameraChange = (payload: { x: number; y: number; scale: number; viewportWidth: number; viewportHeight: number }) => {
	cameraX.value = payload.x
	cameraY.value = payload.y
	cameraScale.value = payload.scale
	viewportWidth.value = payload.viewportWidth
	viewportHeight.value = payload.viewportHeight
}

const onWallsChange = (nextWalls: Wall[]) => {
	walls.value = nextWalls
	if (editMode.value === 'walls') {
		scheduleWallHistoryCommit()
		return
	}
	updateSeatmap()
}

const onWallSelectionChange = (ids: string[]) => {
	selectedWallIds.value = new Set(ids)
}

const onPendingWallStartChange = (point: { x: number; y: number } | null) => {
	pendingWallStart.value = point
}

const closeGuide = () => {
	showGuide.value = false
	if (typeof window !== 'undefined') {
		window.localStorage.setItem(guideStorageKey, showGuideOnStartup.value ? 'show' : 'hidden')
	}
}

const scheduleWallHistoryCommit = () => {
	if (wallHistoryCommitTimer !== null) {
		clearTimeout(wallHistoryCommitTimer)
	}

	wallHistoryCommitTimer = setTimeout(() => {
		wallHistoryCommitTimer = null
		updateSeatmap()
	}, 100)
}

const createSeatmapSnapshot = (): SeatmapSnapshot => {
	return {
		rows: cloneDeep(rows.value),
		walls: cloneDeep(walls.value),
		labels: cloneDeep(labels.value),
		decorativeShapes: cloneDeep(decorativeShapes.value),
		selectedLabelId: selectedLabelId.value,
		selectedShapeId: selectedShapeId.value,
		selectedSeatIds: Array.from(selectedSeatIds.value),
		selectedRowIndices: Array.from(selectedRowIndices.value),
		selectedWallIds: Array.from(selectedWallIds.value),
		pendingWallStart: pendingWallStart.value ? { ...pendingWallStart.value } : null,
		activeRowIndex: activeRowIndex.value,
		editMode: editMode.value,
		showGrid: showGrid.value,
		snapToGrid: snapToGrid.value
	}
}

const applySeatmapSnapshot = (snapshot: SeatmapSnapshot) => {
	if (wallHistoryCommitTimer !== null) {
		clearTimeout(wallHistoryCommitTimer)
		wallHistoryCommitTimer = null
	}

	isRestoringHistory.value = true
	rows.value = cloneDeep(snapshot.rows)
	walls.value = cloneDeep(snapshot.walls)
	labels.value = cloneDeep(snapshot.labels)
	decorativeShapes.value = cloneDeep(snapshot.decorativeShapes ?? [])
	selectedLabelId.value = snapshot.selectedLabelId
	selectedShapeId.value = snapshot.selectedShapeId ?? null
	selectedSeatIds.value = new Set(snapshot.selectedSeatIds)
	lastSelectedSeatId.value = null
	selectedRowIndices.value = new Set(snapshot.selectedRowIndices)
	selectedWallIds.value = new Set(snapshot.selectedWallIds)
	pendingWallStart.value = snapshot.pendingWallStart ? { ...snapshot.pendingWallStart } : null
	activeRowIndex.value = Math.max(0, Math.min(snapshot.activeRowIndex, Math.max(0, snapshot.rows.length - 1)))
	editMode.value = snapshot.editMode
	showGrid.value = snapshot.showGrid
	snapToGrid.value = snapshot.snapToGrid
	isRestoringHistory.value = false
}

const resetHistory = () => {
	if (wallHistoryCommitTimer !== null) {
		clearTimeout(wallHistoryCommitTimer)
		wallHistoryCommitTimer = null
	}

	history.value = [createSeatmapSnapshot()]
	historyIndex.value = 0
}

const recordHistory = () => {
	if (isRestoringHistory.value) {
		return
	}

	const snapshot = createSeatmapSnapshot()

	if (historyIndex.value < history.value.length - 1) {
		history.value = history.value.slice(0, historyIndex.value + 1)
	}

	history.value.push(snapshot)
	const historyLimit = getTotalSeatCount() >= LARGE_MAP_HISTORY_SEAT_THRESHOLD
		? LARGE_MAP_HISTORY_LIMIT
		: MAX_HISTORY
	if (history.value.length > historyLimit) {
		history.value.shift()
	}
	historyIndex.value = history.value.length - 1
}

const scheduleHistoryCommit = () => {
	if (historyCommitTimer !== null) {
		clearTimeout(historyCommitTimer)
	}

	historyCommitTimer = setTimeout(() => {
		historyCommitTimer = null
		recordHistory()
	}, 120)
}

const undo = () => {
	if (!canUndo.value) {
		return
	}

	historyIndex.value -= 1
	const snapshot = history.value[historyIndex.value]
	if (snapshot) {
		applySeatmapSnapshot(snapshot)
	}
}

const initializeDefaultSeatmap = () => {
	gridSize.value = DEFAULT_GRID_SIZE
	walls.value = []
	labels.value = []
	decorativeShapes.value = []
	unsupportedShapes.value = []
	selectedWallIds.value = new Set()
	selectedLabelId.value = null
	selectedShapeId.value = null
	pendingWallStart.value = null
	rows.value = Array.from({ length: DEFAULT_ROW_COUNT }, (_, rowIdx) => {
		const seatsArray = Array.from({ length: DEFAULT_SEATS_PER_ROW }, (_, seatIdx) => ({
			index: seatIdx + 1,
			x: defaultSeatX(seatIdx),
			y: defaultSeatY(rowIdx)
		}))
		const row: Row = {
			seats: seatsArray,
			label: getRowLabel(rowIdx),
			rotation: 0,
			curvature: 0,
			skew: 0,
			startNumber: 1,
			reverseNumbering: false,
			labelPosition: 'start'
		}
		applyRowSeatNumbering(row)
		return row
	})
	relabelRowsNumerically()
		resetHistory()
}

const addRow = () => {
	const newRowIndex = rows.value.length
	const seatsArray = Array.from({ length: DEFAULT_SEATS_PER_ROW }, (_, seatIdx) => ({
		index: seatIdx + 1,
		x: defaultSeatX(seatIdx),
		y: defaultSeatY(newRowIndex)
	}))
	const row: Row = {
		seats: seatsArray,
		label: getRowLabel(newRowIndex),
		rotation: 0,
		curvature: 0,
		skew: 0,
		startNumber: 1,
		reverseNumbering: false,
		labelPosition: 'start'
	}
	applyRowSeatNumbering(row)
	rows.value.push(row)
	activeRowIndex.value = newRowIndex
	lastSelectedRowIndex.value = newRowIndex
	updateSeatmap()
}

const handleUpdateSelectedRowsSeatCount = (count: number) => {
	const targetCount = Math.max(1, Math.round(count || 1))
	const rowIndices = Array.from(selectedRowIndices.value)
	if (rowIndices.length === 0) {
		return
	}

	rowIndices.forEach((rowIndex) => {
		const row = rows.value[rowIndex]
		if (!row || !Array.isArray(row.seats)) {
			return
		}

		const seats = row.seats
		if (seats.length > targetCount) {
			seats.splice(targetCount)
		}

		if (seats.length < targetCount) {
			let lastSeat = seats[seats.length - 1]
			while (seats.length < targetCount) {
				const nextIndex = seats.length
				const nextX = lastSeat ? (lastSeat.x ?? defaultSeatX(nextIndex - 1)) + canvasGridPitch.value.x : defaultSeatX(nextIndex)
				const nextY = lastSeat ? (lastSeat.y ?? defaultSeatY(rowIndex)) : defaultSeatY(rowIndex)
				const nextSeat: Seat = {
					index: nextIndex + 1,
					x: nextX,
					y: nextY
				}
				seats.push(nextSeat)
				lastSeat = nextSeat
			}
		}

		applyRowSeatNumbering(row)
	})

	scheduleRowPropertyUpdate()
}

const handleUpdateSelectedRowsStartNumber = (startNumber: number) => {
	const rowIndices = Array.from(selectedRowIndices.value)
	if (rowIndices.length === 0) {
		return
	}

	const parsed = Number(startNumber)
	const normalized = Number.isFinite(parsed) ? Math.max(1, Math.round(parsed)) : 1

	rowIndices.forEach((rowIndex) => {
		const row = rows.value[rowIndex]
		if (!row) {
			return
		}
		row.startNumber = normalized
		applyRowSeatNumbering(row)
	})

	scheduleRowPropertyUpdate()
}

const handleUpdateSelectedRowsReverseNumbering = (reverseNumbering: boolean) => {
	const rowIndices = Array.from(selectedRowIndices.value)
	if (rowIndices.length === 0) {
		return
	}

	rowIndices.forEach((rowIndex) => {
		const row = rows.value[rowIndex]
		if (!row) {
			return
		}
		row.reverseNumbering = !!reverseNumbering
		applyRowSeatNumbering(row)
	})

	scheduleRowPropertyUpdate()
}

const handleUpdateSelectedRowsLabel = (label: string) => {
	const rowIndices = Array.from(selectedRowIndices.value)
	if (rowIndices.length === 0) {
		return
	}

	rowIndices.forEach((rowIndex) => {
		const row = rows.value[rowIndex]
		if (!row) {
			return
		}
		const trimmed = String(label ?? '').trim()
		row.label = trimmed || getRowLabel(rowIndex)
	})

	scheduleRowPropertyUpdate()
}

const handleUpdateSelectedRowsLabelPosition = (position: RowLabelPosition) => {
	const rowIndices = Array.from(selectedRowIndices.value)
	if (rowIndices.length === 0) {
		return
	}

	const normalized = position === 'end' ? 'end' : 'start'

	rowIndices.forEach((rowIndex) => {
		const row = rows.value[rowIndex]
		if (!row) {
			return
		}
		row.labelPosition = normalized
	})

	scheduleRowPropertyUpdate()
}

const handleUpdateSelectedSeatsTitle = (title: string) => {
	const trimmed = String(title ?? '').trim()
	let changed = false

	Array.from(selectedSeatIds.value).forEach((seatKey) => {
		const parsed = parseSeatKey(seatKey)
		if (!parsed) {
			return
		}
		const row = rows.value[parsed.rowIndex]
		if (!row || !Array.isArray(row.seats)) {
			return
		}
		const seat = row.seats[parsed.seatIndex]
		if (!seat) {
			return
		}
		if (trimmed === '') {
			if (seat.title !== undefined) {
				delete seat.title
				changed = true
			}
			return
		}
		if (seat.title !== trimmed) {
			seat.title = trimmed
			changed = true
		}
	})

	if (changed) {
		updateSeatmap()
	}
}

const handleUpdateSelectedSeatsZone = (zoneId: number | null) => {
	let changed = false

	Array.from(selectedSeatIds.value).forEach((seatKey) => {
		const parsed = parseSeatKey(seatKey)
		if (!parsed) {
			return
		}
		const row = rows.value[parsed.rowIndex]
		if (!row || !Array.isArray(row.seats)) {
			return
		}
		const seat = row.seats[parsed.seatIndex]
		if (!seat) {
			return
		}
		if (zoneId == null) {
			if (seat.zone !== undefined) {
				delete seat.zone
				changed = true
			}
			return
		}
		if (Number(seat.zone) !== zoneId) {
			seat.zone = zoneId
			changed = true
		}
	})

	if (changed) {
		updateSeatmap()
	}
}

const clearSeatZonesMatchingId = (zoneId: number) => {
	let changed = false
	rows.value.forEach((row) => {
		if (!Array.isArray(row.seats)) {
			return
		}
		row.seats.forEach((seat) => {
			if (seat.zone != null && Number(seat.zone) === zoneId) {
				delete seat.zone
				changed = true
			}
		})
	})
	if (changed) {
		updateSeatmap()
	}
}

const getSeatmapZoneApiBase = () => {
	const store = (useNuxtApp() as { $store?: { state?: Record<string, unknown>; getters?: Record<string, unknown> } }).$store
	const state = store?.state ?? {}
	const getters = store?.getters ?? {}
	const baseDomain = String(getters.baseDomain ?? state.baseDomain ?? '')
	const shopId = String(getters.shopId ?? state.shopId ?? '')
	const apiKey = String(getters.apiKey ?? state.apiKey ?? '')
	const version = String(getters.version ?? state.version ?? 'default')
	const versionStr = version !== 'default' ? `&version=${version}` : ''
	return {
		baseDomain,
		shopId,
		apiKey,
		versionStr,
		zonesUrl: `${baseDomain}${shopId}/zones.json?type=${SEATMAP_ZONE_TYPE}&apikey=${apiKey}${versionStr}`
	}
}

const normalizeSeatmapZones = (data: unknown): SeatmapZone[] => {
	if (!Array.isArray(data)) {
		return []
	}
	return data
		.map((item) => {
			if (!item || typeof item !== 'object') {
				return null
			}
			const zone = item as { id?: unknown; name?: unknown; type?: unknown }
			const id = Number(zone.id)
			if (!Number.isFinite(id) || id <= 0) {
				return null
			}
			return {
				id,
				name: zone.name != null ? String(zone.name) : '',
				type: zone.type != null ? Number(zone.type) : SEATMAP_ZONE_TYPE
			}
		})
		.filter((zone): zone is SeatmapZone => !!zone)
		.sort((a, b) => a.name.localeCompare(b.name))
}

const loadSeatmapZones = async () => {
	const { zonesUrl, shopId, apiKey } = getSeatmapZoneApiBase()
	if (!shopId || !apiKey) {
		seatmapZones.value = []
		return
	}
	try {
		const response = await axios.get(zonesUrl)
		seatmapZones.value = normalizeSeatmapZones(response.data)
	} catch (error) {
		console.error('Failed to load seatmap zones', error)
		seatmapZones.value = []
	}
}

const addSeatmapZone = async () => {
	const { baseDomain, shopId, apiKey, versionStr } = getSeatmapZoneApiBase()
	if (!shopId || !apiKey || zonesBusy.value) {
		return
	}
	zonesBusy.value = true
	try {
		const response = await axios.post(
			`${baseDomain}${shopId}/zones.json?apikey=${apiKey}${versionStr}`,
			{ name: 'New zone', type: SEATMAP_ZONE_TYPE }
		)
		const createdId = Number(
			typeof response.data === 'object' && response.data != null && 'id' in response.data
				? (response.data as { id: unknown }).id
				: response.data
		)
		await loadSeatmapZones()
		if (Number.isFinite(createdId) && createdId > 0 && !seatmapZones.value.some((zone) => zone.id === createdId)) {
			seatmapZones.value = [
				...seatmapZones.value,
				{ id: createdId, name: 'New zone', type: SEATMAP_ZONE_TYPE }
			].sort((a, b) => a.name.localeCompare(b.name))
		}
	} catch (error) {
		console.error('Failed to create seatmap zone', error)
	}
	zonesBusy.value = false
}

const renameSeatmapZone = async (payload: { id: number; name: string }) => {
	const { baseDomain, shopId, apiKey, versionStr } = getSeatmapZoneApiBase()
	if (!shopId || !apiKey || zonesBusy.value) {
		return
	}
	const trimmed = String(payload.name ?? '').trim()
	if (!trimmed) {
		return
	}
	zonesBusy.value = true
	try {
		await axios.put(
			`${baseDomain}${shopId}/zones/${payload.id}.json?apikey=${apiKey}${versionStr}`,
			{ id: payload.id, name: trimmed, type: SEATMAP_ZONE_TYPE }
		)
		seatmapZones.value = seatmapZones.value
			.map((zone) => (zone.id === payload.id ? { ...zone, name: trimmed } : zone))
			.sort((a, b) => a.name.localeCompare(b.name))
	} catch (error) {
		console.error('Failed to rename seatmap zone', error)
		await loadSeatmapZones()
	}
	zonesBusy.value = false
}

const deleteSeatmapZone = async (zoneId: number) => {
	const { baseDomain, shopId, apiKey, versionStr } = getSeatmapZoneApiBase()
	if (!shopId || !apiKey || zonesBusy.value) {
		return
	}
	zonesBusy.value = true
	try {
		await axios.delete(`${baseDomain}${shopId}/zones/${zoneId}.json?apikey=${apiKey}${versionStr}`)
		seatmapZones.value = seatmapZones.value.filter((zone) => zone.id !== zoneId)
		clearSeatZonesMatchingId(zoneId)
	} catch (error) {
		console.error('Failed to delete seatmap zone', error)
		await loadSeatmapZones()
	}
	zonesBusy.value = false
}

const updateSelectedRowsNumericSetting = (field: 'rotation' | 'curvature' | 'skew', value: number, min: number, max: number) => {
	const rowIndices = Array.from(selectedRowIndices.value)
	if (rowIndices.length === 0) {
		return
	}

	const parsed = Number(value)
	const normalized = Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : 0

	// Pause expensive calculations
	isCalculationPaused.value = true
	isInteractingWithSlider.value = true

	rowIndices.forEach((rowIndex) => {
		const row = rows.value[rowIndex]
		if (!row) {
			return
		}
		row[field] = normalized
	})

	// Schedule history recording for after slider ends
	scheduleHistoryRecordOnSliderEnd()
	
	// Resume calculations on next frame
	requestAnimationFrame(() => {
		isCalculationPaused.value = false
	})
}


const handleUpdateSelectedRowsRotation = (rotation: number) => {
	updateSelectedRowsNumericSetting('rotation', rotation, -180, 180)
}

const handleUpdateSelectedRowsCurvature = (curvature: number) => {
	updateSelectedRowsNumericSetting('curvature', curvature, -120, 120)
}

const handleUpdateSelectedRowsSkew = (skew: number) => {
	updateSelectedRowsNumericSetting('skew', skew, -120, 120)
}

const resetSelectedRowsToDefault = () => {
	const rowIndices = Array.from(selectedRowIndices.value)
	if (rowIndices.length === 0) {
		return
	}

	rowIndices.forEach((rowIndex) => {
		const row = rows.value[rowIndex]
		if (!row) {
			return
		}

		row.rotation = 0
		row.curvature = 0
		row.skew = 0
		row.startNumber = 1
		row.reverseNumbering = false
		row.label = getRowLabel(rowIndex)
		row.labelPosition = 'start'
		row.seats = Array.from({ length: DEFAULT_SEATS_PER_ROW }, (_, seatIdx) => ({
			index: seatIdx + 1,
			x: defaultSeatX(seatIdx),
			y: defaultSeatY(rowIndex)
		}))
		applyRowSeatNumbering(row)
	})

	scheduleRowPropertyUpdate()
}

const duplicateSelectedRows = () => {
	const rowIndices = Array.from(selectedRowIndices.value).sort((a, b) => a - b)
	if (rowIndices.length === 0) {
		return
	}

	const offset = DEFAULT_GRID_SIZE
	const insertStart = rows.value.length

	rowIndices.forEach((rowIndex) => {
		const row = rows.value[rowIndex]
		if (!row || !Array.isArray(row.seats)) {
			return
		}

		const duplicatedSeats = row.seats.map((seat) => ({
			...cloneSeat(seat),
			x: (seat.x ?? 0) + offset,
			y: (seat.y ?? 0) + offset,
			seatId: undefined
		}))

		const duplicatedRow: Row = {
			label: row.label || getRowLabel(rowIndex),
			rotation: Number(row.rotation ?? 0),
			curvature: Number(row.curvature ?? 0),
			skew: Number(row.skew ?? 0),
			startNumber: Number(row.startNumber ?? 1),
			reverseNumbering: !!row.reverseNumbering,
			labelPosition: getRowLabelPosition(row),
			seats: duplicatedSeats
		}
		applyRowSeatNumbering(duplicatedRow)
		rows.value.push(duplicatedRow)
	})

	const duplicatedCount = rows.value.length - insertStart
	if (duplicatedCount <= 0) {
		return
	}

	relabelRowsNumerically()
	selectedRowIndices.value = new Set(Array.from({ length: duplicatedCount }, (_, idx) => insertStart + idx))
	activeRowIndex.value = insertStart
	lastSelectedRowIndex.value = insertStart + duplicatedCount - 1
	activeSidebar.value = 'row'
	updateSeatmap()
}

const getNextLabelId = () => {
	return labels.value.length > 0 ? Math.max(...labels.value.map((label) => label.id)) + 1 : 1
}

const scheduleRowPropertyUpdate = () => {
	if (rafUpdatePending) {
		return
	}
	
	rafUpdatePending = true
	
	requestAnimationFrame(() => {
		rafUpdatePending = false
		if (!isInteractingWithSlider.value) {
			recordHistory()
		}
	})
}

const scheduleHistoryRecordOnSliderEnd = () => {
	if (sliderEndTimer !== null) {
		clearTimeout(sliderEndTimer)
	}
	
	sliderEndTimer = setTimeout(() => {
		sliderEndTimer = null
		isInteractingWithSlider.value = false
		recordHistory()
	}, 200)
}

const addLabel = () => {
	const id = getNextLabelId()
	labels.value.push({
		id,
		x: DEFAULT_GRID_SIZE * 4,
		y: DEFAULT_GRID_SIZE * 4,
		text: 'Label',
		fontSize: 16,
		fontFamily: 'Segoe UI',
		fill: '#0284c7'
	})
	selectedLabelId.value = id
	selectedShapeId.value = null
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	selectedRowIndices.value = new Set()
	lastSelectedRowIndex.value = null
	selectedWallIds.value = new Set()
	activeSidebar.value = 'label'
	updateSeatmap()
}

const getNextShapeId = () => {
	return `shape-${Date.now()}-${Math.round(Math.random() * 100000)}`
}

const addDecorativeShape = () => {
	const id = getNextShapeId()
	decorativeShapes.value.push({
		id,
		type: 'rrect',
		x: DEFAULT_GRID_SIZE * 4,
		y: DEFAULT_GRID_SIZE * 2,
		w: DEFAULT_SHAPE_WIDTH,
		h: DEFAULT_SHAPE_HEIGHT,
		r: DEFAULT_SHAPE_CORNER_RADIUS,
		fill: DEFAULT_SHAPE_FILL,
		border: null,
		text: null
	})
	selectedShapeId.value = id
	selectedLabelId.value = null
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	selectedRowIndices.value = new Set()
	lastSelectedRowIndex.value = null
	selectedWallIds.value = new Set()
	activeSidebar.value = 'shape'
	updateSeatmap()
}

const handleSelectShape = (id: string) => {
	if (editMode.value !== 'seats') {
		return
	}
	selectedShapeId.value = id
	selectedLabelId.value = null
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	selectedRowIndices.value = new Set()
	selectedWallIds.value = new Set()
	activeSidebar.value = 'shape'
}

const handleShapePositionChanged = (shapeId: string, x: number, y: number) => {
	if (editMode.value !== 'seats') {
		return
	}

	const shape = decorativeShapes.value.find((item) => item.id === shapeId)
	if (!shape) {
		return
	}

	shape.x = x
	shape.y = y
	updateSeatmap()
}

const handleShapeResizeChanged = (shapeId: string, w: number, h: number) => {
	if (editMode.value !== 'seats') {
		return
	}

	const shape = decorativeShapes.value.find((item) => item.id === shapeId)
	if (!shape) {
		return
	}

	shape.w = Math.max(MIN_SHAPE_SIZE, Math.round(w))
	shape.h = Math.max(MIN_SHAPE_SIZE, Math.round(h))
	updateSeatmap()
}

const handleUpdateShapeWidth = (width: number) => {
	const shape = selectedShape.value
	if (!shape) {
		return
	}
	shape.w = Math.max(MIN_SHAPE_SIZE, Math.round(width) || MIN_SHAPE_SIZE)
	updateSeatmap()
}

const handleUpdateShapeHeight = (height: number) => {
	const shape = selectedShape.value
	if (!shape) {
		return
	}
	shape.h = Math.max(MIN_SHAPE_SIZE, Math.round(height) || MIN_SHAPE_SIZE)
	updateSeatmap()
}

const handleUpdateShapeCornerRadius = (radius: number) => {
	const shape = selectedShape.value
	if (!shape) {
		return
	}
	shape.r = Math.max(0, Math.round(radius) || 0)
	updateSeatmap()
}

const handleUpdateShapeFill = (fill: string) => {
	const shape = selectedShape.value
	if (!shape) {
		return
	}
	shape.fill = fill
	updateSeatmap()
}

const handleUpdateShapeBorder = (border: string | null) => {
	const shape = selectedShape.value
	if (!shape) {
		return
	}
	shape.border = border
	updateSeatmap()
}

const handleUpdateShapeText = (text: string) => {
	const shape = selectedShape.value
	if (!shape) {
		return
	}
	shape.text = text
	updateSeatmap()
}

const deleteSelectedShape = () => {
	if (selectedShapeId.value === null) {
		return false
	}

	decorativeShapes.value = decorativeShapes.value.filter((shape) => shape.id !== selectedShapeId.value)
	selectedShapeId.value = null
	if (activeSidebar.value === 'shape') {
		activeSidebar.value = null
	}
	updateSeatmap()
	return true
}

const addSeatToRow = (rowIndex: number) => {
	const row = rows.value[rowIndex]
	if (!row || !Array.isArray(row.seats)) {
		return
	}

	const seatList = row.seats
	const lastSeat = seatList[seatList.length - 1]
	const nextSeatX = lastSeat ? (lastSeat.x ?? defaultSeatX(seatList.length - 1)) + canvasGridPitch.value.x : defaultSeatX(0)
	const nextSeatY = lastSeat ? (lastSeat.y ?? defaultSeatY(rowIndex)) : defaultSeatY(rowIndex)

	seatList.push({
		index: seatList.length + 1,
		x: nextSeatX,
		y: nextSeatY
	})
	applyRowSeatNumbering(row)
	activeRowIndex.value = rowIndex
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	selectedRowIndices.value = new Set([rowIndex])
	lastSelectedRowIndex.value = rowIndex
	updateSeatmap()
}

const removeRow = (index: number) => {
	if (rows.value.length > 1) {
		rows.value.splice(index, 1)
		relabelRowsNumerically()
		selectedSeatIds.value = new Set()
		lastSelectedSeatId.value = null
		selectedRowIndices.value = new Set()
		lastSelectedRowIndex.value = null
		rowDragState.value = null
		if (activeRowIndex.value >= rows.value.length) {
			activeRowIndex.value = rows.value.length - 1
		}
		updateSeatmap()
	}
}

const deleteSelection = () => {
	if (deleteSelectedSeats()) {
		return
	}
	if (deleteSelectedWalls()) {
		return
	}
	if (deleteSelectedRows()) {
		return
	}
	if (deleteSelectedShape()) {
		return
	}
	deleteSelectedLabel()
}

const removeSeat = (rowIndex: number, seatIndex: number) => {
	const row = rows.value[rowIndex]
	if (!row || !Array.isArray(row.seats)) {
		return
	}

	if (row.seats.length <= 1) {
		removeRow(rowIndex)
		return
	}

	row.seats.splice(seatIndex, 1)
	applyRowSeatNumbering(row)
	updateSeatmap()
}

const cloneSeat = (seat: Seat): Seat => {
	return {
		index: seat.index,
		title: seat.title,
		x: seat.x,
		y: seat.y,
		seatId: seat.seatId,
		disabled: !!seat.disabled,
		zone: seat.zone,
		bg: seat.bg,
		linkedSeats: seat.linkedSeats ? [...seat.linkedSeats] : undefined,
		w: seat.w,
		h: seat.h,
		r: seat.r
	}
}

const collectSelectedRowTargets = () => {
	const targets: Array<{ id: string; seat: Seat }> = []

	if (selectedSeatIds.value.size > 0) {
		Array.from(selectedSeatIds.value)
			.forEach((seatKey) => {
				const parsedSeat = parseSeatKey(seatKey)
				if (!parsedSeat) {
					return
				}

				const row = rows.value[parsedSeat.rowIndex]
				if (!row || !Array.isArray(row.seats)) {
					return
				}

				const seat = row.seats[parsedSeat.seatIndex]
				if (!seat) {
					return
				}

				targets.push({ id: seatKey, seat })
			})

		return targets
	}

	Array.from(selectedRowIndices.value)
		.sort((a, b) => a - b)
		.forEach((rowIndex) => {
			const row = rows.value[rowIndex]
			if (!row || !Array.isArray(row.seats)) {
				return
			}

			row.seats.forEach((seat, seatIndex) => {
				targets.push({ id: `${rowIndex}-${seatIndex}`, seat })
			})
		})

	return targets
}

const disableSelectionButtonLabel = computed(() => {
	const targets = collectSelectedRowTargets()
	if (targets.length === 0) {
		return _('Disable Selected')
	}

	const allDisabled = targets.every((target) => !!target.seat.disabled)
	return allDisabled ? _('Enable Selected') : _('Disable Selected')
})

const toggleDisableSelection = () => {
	if (editMode.value !== 'seats') {
		return
	}

	const targets = collectSelectedRowTargets()
	if (targets.length === 0) {
		return
	}

	const allDisabled = targets.every((target) => !!target.seat.disabled)
	const nextDisabledState = !allDisabled

	targets.forEach((target) => {
		target.seat.disabled = nextDisabledState
	})

	updateSeatmap()
}

const isLabelSelected = (id: number) => {
	return selectedLabelId.value === id
}

const handleSelectLabel = (id: number) => {
	if (editMode.value !== 'seats') {
		return
	}
	selectedLabelId.value = id
	selectedShapeId.value = null
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	selectedRowIndices.value = new Set()
	selectedWallIds.value = new Set()
	activeSidebar.value = 'label'
}

const handleLabelPositionChanged = (labelId: number, x: number, y: number) => {
	if (editMode.value !== 'seats') {
		return
	}

	const label = labels.value.find((item) => item.id === labelId)
	if (!label) {
		return
	}

	label.x = x
	label.y = y
	updateSeatmap()
}

const handleUpdateLabelText = (text: string) => {
	if (selectedLabelId.value === null) {
		return
	}

	const label = labels.value.find((item) => item.id === selectedLabelId.value)
	if (!label) {
		return
	}

	label.text = text
	updateSeatmap()
}

const handleUpdateLabelFontSize = (fontSize: number) => {
	if (selectedLabelId.value === null) {
		return
	}

	const label = labels.value.find((item) => item.id === selectedLabelId.value)
	if (!label) {
		return
	}

	label.fontSize = Math.max(8, fontSize)
	updateSeatmap()
}

const handleUpdateLabelFontFamily = (fontFamily: string) => {
	if (selectedLabelId.value === null) {
		return
	}

	const label = labels.value.find((item) => item.id === selectedLabelId.value)
	if (!label) {
		return
	}

	label.fontFamily = fontFamily
	updateSeatmap()
}

const handleUpdateLabelColor = (fill: string) => {
	if (selectedLabelId.value === null) {
		return
	}

	const label = labels.value.find((item) => item.id === selectedLabelId.value)
	if (!label) {
		return
	}

	label.fill = fill
	updateSeatmap()
}

const deleteSelectedLabel = () => {
	if (selectedLabelId.value === null) {
		return false
	}

	labels.value = labels.value.filter((label) => label.id !== selectedLabelId.value)
	selectedLabelId.value = null
	if (activeSidebar.value === 'label') {
		activeSidebar.value = null
	}
	updateSeatmap()
	return true
}

const deleteSelectedWalls = () => {
	const wallIds = Array.from(selectedWallIds.value)
	if (wallIds.length === 0) {
		return false
	}

	walls.value = walls.value.filter((wall) => !selectedWallIds.value.has(wall.id))
	selectedWallIds.value = new Set()
	pendingWallStart.value = null
	updateSeatmap()
	return true
}

const deleteSelectedSeats = () => {
	const selectedKeys = Array.from(selectedSeatIds.value)
	if (selectedKeys.length === 0) {
		return false
	}

	const groupedSeatIndices = new Map<number, number[]>()

	selectedKeys.forEach((seatKey) => {
		const parsedSeat = parseSeatKey(seatKey)
		if (!parsedSeat) {
			return
		}

		const row = rows.value[parsedSeat.rowIndex]
		if (!row || !Array.isArray(row.seats) || parsedSeat.seatIndex < 0 || parsedSeat.seatIndex >= row.seats.length) {
			return
		}

		const seatIndices = groupedSeatIndices.get(parsedSeat.rowIndex)
		if (seatIndices) {
			seatIndices.push(parsedSeat.seatIndex)
		} else {
			groupedSeatIndices.set(parsedSeat.rowIndex, [parsedSeat.seatIndex])
		}
	})

	if (groupedSeatIndices.size === 0) {
		selectedSeatIds.value = new Set()
		lastSelectedSeatId.value = null
		return false
	}

	const rowsToDelete: number[] = []

	groupedSeatIndices.forEach((seatIndices, rowIndex) => {
		const row = rows.value[rowIndex]
		if (!row || !Array.isArray(row.seats)) {
			return
		}
		const rowSeats = row.seats

		const uniqueSeatIndicesDescending = Array.from(new Set(seatIndices)).sort((a, b) => b - a)
		uniqueSeatIndicesDescending.forEach((seatIndex) => {
			if (seatIndex >= 0 && seatIndex < rowSeats.length) {
				rowSeats.splice(seatIndex, 1)
			}
		})

		if (rowSeats.length === 0) {
			rowsToDelete.push(rowIndex)
			return
		}

		applyRowSeatNumbering(row)
	})

	rowsToDelete.sort((a, b) => b - a).forEach((rowIndex) => {
		if (rows.value.length > 1 && rowIndex >= 0 && rowIndex < rows.value.length) {
			rows.value.splice(rowIndex, 1)
		}
	})

	if (rows.value.length === 0) {
		const fallbackRow: Row = {
			seats: [{ index: 1, x: defaultSeatX(0), y: defaultSeatY(0) }],
			label: getRowLabel(0),
			rotation: 0,
			curvature: 0,
			skew: 0,
			startNumber: 1,
			reverseNumbering: false,
			labelPosition: 'start'
		}
		applyRowSeatNumbering(fallbackRow)
		rows.value.push(fallbackRow)
	}

	relabelRowsNumerically()
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	selectedRowIndices.value = new Set()
	lastSelectedRowIndex.value = null
	if (activeSidebar.value === 'seat') {
		activeSidebar.value = null
	}
	activeRowIndex.value = Math.max(0, Math.min(activeRowIndex.value, rows.value.length - 1))
	rowDragState.value = null
	updateSeatmap()
	return true
}

const deleteSelectedRows = () => {
	const rowIndices = Array.from(selectedRowIndices.value).sort((a, b) => b - a)
	if (rowIndices.length === 0) {
		return false
	}

	rowIndices.forEach((rowIndex) => {
		if (rows.value.length > 1 && rowIndex >= 0 && rowIndex < rows.value.length) {
			rows.value.splice(rowIndex, 1)
		}
	})

	relabelRowsNumerically()
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	selectedRowIndices.value = new Set()
	lastSelectedRowIndex.value = null
	if (activeSidebar.value === 'row') {
		activeSidebar.value = null
	}
	rowDragState.value = null
	activeRowIndex.value = Math.max(0, Math.min(activeRowIndex.value, rows.value.length - 1))
	updateSeatmap()
	return true
}

const copySelection = () => {
	const rowIndices = Array.from(selectedRowIndices.value).sort((a, b) => a - b)
	if (rowIndices.length > 0) {
		const items: RowClipboardItem[] = []
		rowIndices.forEach((rowIndex) => {
			const row = rows.value[rowIndex]
			if (!row || !Array.isArray(row.seats)) {
				return
			}
			items.push({
				seats: row.seats.map((seat: Seat) => cloneSeat(seat)),
				rotation: Number(row.rotation ?? 0),
				curvature: Number(row.curvature ?? 0),
				skew: Number(row.skew ?? 0),
				startNumber: Number(row.startNumber ?? 1),
				reverseNumbering: !!row.reverseNumbering,
				label: row.label || getRowLabel(rowIndex),
				labelPosition: getRowLabelPosition(row)
			})
		})

		if (items.length > 0) {
			clipboardData.value = { kind: 'rows', items }
			pasteStep.value = 1
			return true
		}
	}

	return false
}

const pasteSelection = () => {
	if (!clipboardData.value) {
		return false
	}

	const offset = DEFAULT_GRID_SIZE * pasteStep.value

	const startIndex = rows.value.length
	clipboardData.value.items.forEach((item) => {
		const clonedSeats = item.seats.map((seat) => ({
			...cloneSeat(seat),
			x: (seat.x ?? 0) + offset,
			y: (seat.y ?? 0) + offset,
			seatId: undefined
		}))
		const pastedRow: Row = {
			seats: clonedSeats,
			label: item.label || '',
			rotation: item.rotation,
			curvature: item.curvature,
			skew: item.skew,
			startNumber: item.startNumber ?? 1,
			reverseNumbering: !!item.reverseNumbering,
			labelPosition: item.labelPosition === 'end' ? 'end' : 'start'
		}
		applyRowSeatNumbering(pastedRow)
		rows.value.push(pastedRow)
	})

	relabelRowsNumerically()
	selectedRowIndices.value = new Set(
		Array.from({ length: clipboardData.value.items.length }, (_, i) => startIndex + i)
	)
	activeRowIndex.value = startIndex
	lastSelectedRowIndex.value = startIndex + clipboardData.value.items.length - 1
	updateSeatmap()
	pasteStep.value += 1
	return true
}

const isEditableTarget = (target: EventTarget | null) => {
	const el = target as HTMLElement | null
	if (!el) {
		return false
	}
	const tagName = el.tagName?.toLowerCase()
	return tagName === 'input' || tagName === 'textarea' || tagName === 'select' || el.isContentEditable
}

const onEditorKeyDown = (event: KeyboardEvent) => {
	if (isEditableTarget(event.target)) {
		return
	}

	const key = event.key.toLowerCase()
	const hasModifier = event.ctrlKey || event.metaKey

	if (!hasModifier && (event.key === 'Delete' || event.key === 'Backspace')) {
		const deleted = deleteSelectedSeats() || deleteSelectedWalls() || deleteSelectedRows() || deleteSelectedShape() || deleteSelectedLabel()
		if (deleted) {
			event.preventDefault()
		}
		return
	}

	if (hasModifier && key === 'c') {
		const copied = copySelection()
		if (copied) {
			event.preventDefault()
		}
		return
	}

	if (hasModifier && key === 'z' && !event.shiftKey) {
		if (canUndo.value) {
			undo()
			event.preventDefault()
		}
		return
	}

	if (hasModifier && key === 'v') {
		const pasted = pasteSelection()
		if (pasted) {
			event.preventDefault()
		}
	}
}

const updateSeatmap = () => {
	if (getTotalSeatCount() >= MEDIUM_MAP_DEBOUNCED_HISTORY_SEAT_THRESHOLD) {
		scheduleHistoryCommit()
		return
	}
	recordHistory()
}

const triggerImport = () => {
	importInputRef.value?.click()
}

const buildRowsFromImportedSeats = (importedSeats: ImportSeat[]) => {
	const bucketSize = 8
	const buckets = new Map<number, ImportSeat[]>()

	for (const seat of importedSeats) {
		const x = Number(seat.x)
		const y = Number(seat.y)
		if (!Number.isFinite(x) || !Number.isFinite(y)) {
			continue
		}

		const bucketKey = Math.round(y / bucketSize)
		const bucket = buckets.get(bucketKey)
		if (bucket) {
			bucket.push(seat)
		} else {
			buckets.set(bucketKey, [seat])
		}
	}

	const sortedBucketKeys = Array.from(buckets.keys()).sort((a, b) => a - b)

	rows.value = sortedBucketKeys.map((bucketKey, rowIdx) => {
		const bucketSeats = buckets.get(bucketKey) ?? []
		const rowSeats = [...bucketSeats].sort((a, b) => a.x - b.x)

		const seats: Seat[] = rowSeats.map((seat, seatIdx) => {
			const titleStr = seat.title == null ? '' : String(seat.title).trim()
			const isNumericTitle = /^\d+$/.test(titleStr) && Number(titleStr) > 0
			const index = isNumericTitle ? Number(titleStr) : seatIdx + 1
			return {
				index,
				title: isNumericTitle || titleStr === '' ? undefined : titleStr,
				x: seat.x,
				y: seat.y,
				seatId: seat.seat,
				disabled: !!seat.disabled,
				zone: seat.zone,
				bg: seat.bg,
				linkedSeats: seat.linkedSeats,
				w: seat.w,
				h: seat.h,
				r: seat.r
			}
		})

		const headerValue = rowSeats
			.map((seat) => seat.header)
			.find((header) => header != null && String(header).trim() !== '')

		const importedRow: Row = {
			label: headerValue != null ? String(headerValue) : getRowLabel(rowIdx),
			rotation: 0,
			curvature: 0,
			skew: 0,
			labelPosition: 'start',
			seats
		}
		applyRowSeatNumbering(importedRow)
		return importedRow
	})
	relabelRowsNumerically()

	activeRowIndex.value = 0
	selectedRowIndices.value = new Set()
}

const buildWallsFromImportedShapes = (importedShapes: ImportShape[] = []) => {
	let nextWallId = 1
	let nextShapeId = 1
	const parsedWalls: Wall[] = []
	const parsedShapes: DecorativeShape[] = []
	const otherShapes: ImportShape[] = []

	importedShapes.forEach((shape) => {
		if (!shape) {
			return
		}

		if (shape.type === 'line' && Array.isArray(shape.points) && shape.points.length >= 4) {
			parsedWalls.push({
				id: `wall-import-${nextWallId++}`,
				x1: Number(shape.points[0]),
				y1: Number(shape.points[1]),
				x2: Number(shape.points[2]),
				y2: Number(shape.points[3]),
				stroke: shape.stroke,
				strokeWidth: shape.strokeWidth
			})
			return
		}

		if (shape.type === 'rect' || shape.type === 'rrect') {
			// A decorative rect/rrect (e.g. a stage or screen) - editable via the shapes UI.
			parsedShapes.push({
				id: `shape-import-${nextShapeId++}`,
				type: shape.type,
				x: Number(shape.x) || 0,
				y: Number(shape.y) || 0,
				w: Number(shape.w) || DEFAULT_SHAPE_WIDTH,
				h: Number(shape.h) || DEFAULT_SHAPE_HEIGHT,
				r: Number(shape.r) || 0,
				fill: typeof shape.fill === 'string' ? shape.fill : DEFAULT_SHAPE_FILL,
				border: typeof shape.border === 'string' ? shape.border : null,
				text: typeof shape.text === 'string' ? shape.text : null,
				subtype: typeof shape.subtype === 'string' ? shape.subtype : undefined
			})
			return
		}

		// Some other shape kind we don't understand at all - keep it as-is so it
		// survives the round trip instead of being dropped.
		otherShapes.push(shape)
	})

	walls.value = parsedWalls.filter((wall) =>
		Number.isFinite(wall.x1)
		&& Number.isFinite(wall.y1)
		&& Number.isFinite(wall.x2)
		&& Number.isFinite(wall.y2)
	)
	decorativeShapes.value = parsedShapes
	unsupportedShapes.value = otherShapes
	selectedWallIds.value = new Set()
	selectedShapeId.value = null
	pendingWallStart.value = null
}

const getSeatmapContentBounds = () => {
	let minX = Number.POSITIVE_INFINITY
	let minY = Number.POSITIVE_INFINITY
	let maxX = Number.NEGATIVE_INFINITY
	let maxY = Number.NEGATIVE_INFINITY

	const includePoint = (x: number, y: number) => {
		if (!Number.isFinite(x) || !Number.isFinite(y)) {
			return
		}
		minX = Math.min(minX, x)
		minY = Math.min(minY, y)
		maxX = Math.max(maxX, x)
		maxY = Math.max(maxY, y)
	}

	const includeRect = (x: number, y: number, width: number, height: number) => {
		includePoint(x, y)
		includePoint(x + width, y + height)
	}

	rows.value.forEach((row, rowIndex) => {
		if (!Array.isArray(row.seats)) {
			return
		}

		row.seats.forEach((seat, seatIndex) => {
			const { x, y } = getSeatPosition(rowIndex, seatIndex, seat)
			includeRect(x, y, seat.w ?? DEFAULT_SEAT_SIZE, seat.h ?? DEFAULT_SEAT_SIZE)
		})
	})

	walls.value.forEach((wall) => {
		includePoint(wall.x1, wall.y1)
		includePoint(wall.x2, wall.y2)
	})

	labels.value.forEach((label) => {
		includePoint(label.x, label.y)
	})

	decorativeShapes.value.forEach((shape) => {
		includeRect(shape.x, shape.y, shape.w, shape.h)
	})

	if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
		return null
	}

	return { minX, minY, maxX, maxY }
}

const focusImportedSeatmap = async () => {
	await nextTick()
	// Wait for the canvas host element to actually be measured (it may not have a
	// real size yet on the very first paint after mounting/importing), otherwise
	// focusWorldBounds() would compute its fit against a 0x0 viewport.
	await new Promise<void>((resolve) => {
		const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb: FrameRequestCallback) => setTimeout(cb, 16)
		raf(() => raf(() => resolve()))
	})

	// Fit to the seat map's own authored canvas size (like the production seat map
	// preview does) rather than just the tight bounding box of seats/walls/labels,
	// so the editor opens showing the entire room the same way the preview does.
	const canvasSize = importedCanvasSize.value
	const bounds = (canvasSize && Number.isFinite(canvasSize.w) && Number.isFinite(canvasSize.h) && (canvasSize.w as number) > 0 && (canvasSize.h as number) > 0)
		? { minX: 0, minY: 0, maxX: canvasSize.w as number, maxY: canvasSize.h as number }
		: getSeatmapContentBounds()

	if (!bounds) {
		gridRef.value?.resetZoom?.()
		return
	}

	gridRef.value?.focusWorldBounds?.(bounds, 88)
}

const hydrateFromImportedData = (parsed: ImportSeatMap) => {
	if ('rows' in parsed && Array.isArray(parsed.rows)) {
		rows.value = normalizeRows(parsed.rows)
		relabelRowsNumerically()
		activeRowIndex.value = 0
		selectedRowIndices.value = new Set()
		selectedLabelId.value = null
	} else if ('seats' in parsed && Array.isArray(parsed.seats)) {
		buildRowsFromImportedSeats(parsed.seats)
	} else {
		throw new Error('Unsupported seat map format')
	}

	if (Array.isArray(parsed.labels)) {
		labels.value = parsed.labels
	} else {
		labels.value = []
	}
	selectedLabelId.value = null

	if (Array.isArray(parsed.shapes)) {
		buildWallsFromImportedShapes(parsed.shapes)
	} else {
		walls.value = []
		decorativeShapes.value = []
		unsupportedShapes.value = []
		selectedWallIds.value = new Set()
		selectedShapeId.value = null
		pendingWallStart.value = null
	}

	importedCanvasSize.value = parsed.canvas ?? null
}

/**
 * Clear resource/shop-bound seat ids so an exported map can be imported as a
 * layout template on another resource. Called only for explicit file Import —
 * not when opening an existing map via initialData.
 */
const stripImportedSeatBindings = (parsed: ImportSeatMap): ImportSeatMap => {
	const clone = cloneSerializable(parsed)

	if (Array.isArray(clone.seats)) {
		clone.seats = clone.seats.map((seat) => {
			const next = { ...seat }
			delete next.seat
			delete next.linkedSeats
			return next
		})
	}

	if (Array.isArray(clone.rows)) {
		clone.rows = clone.rows.map((row) => {
			if (!Array.isArray(row.seats)) {
				return row
			}
			return {
				...row,
				seats: row.seats.map((seat) => {
					const next = { ...seat }
					delete next.seatId
					delete next.linkedSeats
					return next
				})
			}
		})
	}

	return clone
}

const onImportFileChange = async (event: Event) => {
	const input = event.target as HTMLInputElement | null
	const file = input?.files?.[0]
	if (!file) {
		return
	}

	try {
		const content = await file.text()
		const parsed = stripImportedSeatBindings(JSON.parse(content) as ImportSeatMap)
		hydrateFromImportedData(parsed)
		resetHistory()
		await focusImportedSeatmap()
	} catch (error) {
		console.error('Failed to import seatmap JSON', error)
	} finally {
		if (input) {
			input.value = ''
		}
	}
}

const getRowAddSeatPosition = (rowIndex: number) => {
	const seats = getRowSeats(rowIndex)
	if (seats.length === 0) {
		return {
			x: defaultSeatX(0),
			y: defaultSeatY(rowIndex)
		}
	}

	const lastSeatIndex = seats.length - 1
	const lastSeat = seats[lastSeatIndex]
	if (!lastSeat) {
		return {
			x: defaultSeatX(0),
			y: defaultSeatY(rowIndex)
		}
	}

	const lastSeatPosition = getSeatPosition(rowIndex, lastSeatIndex, lastSeat)

	return {
		x: lastSeatPosition.x + canvasGridPitch.value.x,
		y: lastSeatPosition.y
	}
}

const shouldShowAddSeatButton = (rowIndex: number) => {
	return editMode.value === 'seats' && seatEditMode.value === 'rows' && isRowSelected(rowIndex)
}

const getAddSeatButtonConfig = (rowIndex: number) => {
	const position = getRowAddSeatPosition(rowIndex)
	return {
		x: position.x,
		y: position.y,
		draggable: false,
		listening: true
	}
}

const getRowDeleteSeatButtonPosition = (rowIndex: number) => {
	const seats = getRowSeats(rowIndex)
	if (seats.length === 0) {
		return {
			x: defaultSeatX(0) + seatSize - 10,
			y: defaultSeatY(rowIndex) - 10
		}
	}

	const lastSeatIndex = seats.length - 1
	const lastSeat = seats[lastSeatIndex]
	if (!lastSeat) {
		return {
			x: defaultSeatX(0) + seatSize - 10,
			y: defaultSeatY(rowIndex) - 10
		}
	}

	const lastSeatPosition = getSeatPosition(rowIndex, lastSeatIndex, lastSeat)
	return {
		x: lastSeatPosition.x + seatSize - 10,
		y: lastSeatPosition.y - 10
	}
}

const shouldShowDeleteSeatButton = (rowIndex: number) => {
	if (editMode.value !== 'seats' || seatEditMode.value !== 'rows' || !isRowSelected(rowIndex)) {
		return false
	}

	const seats = getRowSeats(rowIndex)
	if (seats.length > 1) {
		return true
	}

	return rows.value.length > 1
}

const getDeleteSeatButtonConfig = (rowIndex: number) => {
	const position = getRowDeleteSeatButtonPosition(rowIndex)
	return {
		x: position.x,
		y: position.y,
		draggable: false,
		listening: true
	}
}

const getAddSeatButtonRectConfig = () => {
	return ADD_SEAT_BUTTON_RECT_CONFIG
}

const getAddSeatButtonTextConfig = () => {
	return ADD_SEAT_BUTTON_TEXT_CONFIG
}

const getDeleteSeatButtonRectConfig = () => {
	return DELETE_SEAT_BUTTON_RECT_CONFIG
}

const getDeleteSeatButtonBinBodyConfig = () => {
	return DELETE_SEAT_BUTTON_BIN_BODY_CONFIG
}

const getDeleteSeatButtonBinLidConfig = () => {
	return DELETE_SEAT_BUTTON_BIN_LID_CONFIG
}

const getDeleteSeatButtonBinHandleConfig = () => {
	return DELETE_SEAT_BUTTON_BIN_HANDLE_CONFIG
}

const onAddSeatButtonPointerDown = (event: any) => {
	stopKonvaEvent(event)
}

const onAddSeatButtonClick = (rowIndex: number, event: any) => {
	stopKonvaEvent(event)
	addSeatToRow(rowIndex)
}

const onDeleteSeatButtonPointerDown = (event: any) => {
	stopKonvaEvent(event)
}

const onDeleteSeatButtonClick = (rowIndex: number, event: any) => {
	stopKonvaEvent(event)
	const seats = getRowSeats(rowIndex)
	if (seats.length === 0) {
		return
	}
	removeSeat(rowIndex, seats.length - 1)
}

const shouldShowSelectedSeatDeleteButton = (rowIndex: number, seatIndex: number) => {
	return editMode.value === 'seats'
		&& seatEditMode.value === 'seats'
		&& isSeatSelected(rowIndex, seatIndex)
}

const getSelectedSeatDeleteButtonConfig = () => {
	return SELECTED_SEAT_DELETE_BUTTON_CONFIG
}

const onSelectedSeatDeleteButtonPointerDown = (event: any) => {
	stopKonvaEvent(event)
}

const onSelectedSeatDeleteButtonClick = (event: any) => {
	stopKonvaEvent(event)
	deleteSelectedSeats()
}

const getSeatConfig = (rowIndex: number, seatIndex: number) => {
	const row = rows.value[rowIndex]
	if (!row) {
		return {
			x: defaultSeatX(seatIndex),
			y: defaultSeatY(rowIndex),
			draggable: false,
			listening: editMode.value === 'seats'
		}
	}

	const seat = getRowSeats(rowIndex)[seatIndex]
	const position = seat ? getSeatPosition(rowIndex, seatIndex, seat) : {
		x: defaultSeatX(seatIndex),
		y: defaultSeatY(rowIndex)
	}
	const margin = getSeatMargin(seat)

	return {
		x: position.x + margin.x,
		y: position.y + margin.y,
		draggable: editMode.value === 'seats' && seatEditMode.value === 'rows',
		listening: editMode.value === 'seats'
	}
}

const getSeatRectConfig = (rowIndex: number, seatIndex: number, seat: Seat) => {
	const seatSelected = isSeatSelected(rowIndex, seatIndex)
	const seatDisabled = !!seat.disabled
	const rowHighlighted = isRowHighlighted(rowIndex)
	const rowDragging = isRowDragging(rowIndex)
	const isLite = shouldUseLiteSeatRendering.value
	// A seat without a seatId has no matching row in Johku's `seat` table yet - it
	// was added during this editing session and hasn't been saved/reconciled. Mark
	// it with a dashed border so users are guided towards removing these first if
	// they want to delete seats, rather than seats that already exist in the system.
	const seatIsUnsaved = !seat.seatId
	// Honor the seat's own imported width/height/corner-radius (if any) instead of
	// always forcing the editor's default size, so seats keep looking the same as
	// they do in the production seat map preview.
	const seatWidth = seat.w ?? seatSize
	const seatHeight = seat.h ?? seatSize
	const seatCornerRadius = seat.r ?? DEFAULT_SEAT_CORNER_RADIUS
	const cacheKey = `${rowIndex}:${seatIndex}:${seatSelected ? 1 : 0}:${seatDisabled ? 1 : 0}:${rowHighlighted ? 1 : 0}:${rowDragging ? 1 : 0}:${isLite ? 1 : 0}:${seatIsUnsaved ? 1 : 0}:${seatWidth}:${seatHeight}:${seatCornerRadius}`
	const cachedConfig = seatRectConfigCache.get(cacheKey)
	if (cachedConfig) {
		return cachedConfig
	}

	const seatFill = seatDisabled
		? (seatSelected ? '#dbeafe' : '#e5e7eb')
		: (seatSelected ? '#dbeafe' : (rowDragging ? '#93c5fd' : rowHighlighted ? '#dbeafe' : '#e2e8f0'))
	const seatStroke = seatDisabled
		? (seatSelected ? '#2563eb' : (rowDragging ? '#6b7280' : rowHighlighted ? '#4b5563' : '#9ca3af'))
		: (seatSelected ? '#2563eb' : (rowDragging ? '#1d4ed8' : rowHighlighted ? '#2563eb' : '#94a3b8'))
	const seatStrokeWidth = 2
	const sharedSeatConfig: Record<string, unknown> = {
		x: 0,
		y: 0,
		width: seatWidth,
		height: seatHeight,
		fill: seatFill,
		stroke: seatStroke,
		strokeWidth: seatStrokeWidth,
		dash: seatIsUnsaved ? [4, 3] : undefined,
		cornerRadius: seatCornerRadius,
		perfectDrawEnabled: false
	}

	seatRectConfigCache.set(cacheKey, sharedSeatConfig)
	trimRenderConfigCache(seatRectConfigCache)
	return sharedSeatConfig
}

const getSeatTextConfig = (rowIndex: number, seatIndex: number, seat: Seat) => {
	const seatDisabled = !!seat.disabled
	const rowHighlighted = isRowHighlighted(rowIndex)
	const seatIndexLabel = getSeatDisplayTitle(seat, seatIndex)
	const fontSize = seatTextFontSize.value
	const seatWidth = seat.w ?? seatSize
	const seatHeight = seat.h ?? seatSize
	const cacheKey = `${rowIndex}:${seatIndex}:${seatDisabled ? 1 : 0}:${rowHighlighted ? 1 : 0}:${seatIndexLabel}:${fontSize}:${seatWidth}:${seatHeight}`
	const cachedConfig = seatTextConfigCache.get(cacheKey)
	if (cachedConfig) {
		return cachedConfig
	}

	const textConfig: Record<string, unknown> = {
		x: 0,
		y: 0,
		width: seatWidth,
		height: seatHeight,
		text: String(seatIndexLabel),
		align: 'center',
		verticalAlign: 'middle',
		fontSize,
		fontFamily: 'Arial',
		fontStyle: 'bold',
		fill: seatDisabled ? '#6b7280' : (rowHighlighted ? '#1e3a8a' : '#334155'),
		listening: false,
		perfectDrawEnabled: false
	}

	seatTextConfigCache.set(cacheKey, textConfig)
	trimRenderConfigCache(seatTextConfigCache)
	return textConfig
}

const shouldShowSeatText = (rowIndex: number, seatIndex: number) => {
	if (shouldRenderAllSeatText.value) {
		return true
	}

	if (selectedRowIndices.value.has(rowIndex) || isRowDragging(rowIndex)) {
		return true
	}

	void seatIndex
	return false
}

// Snaps `value` to the nearest multiple of `pitch` away from `offset`, but also
// considers the "half pitch" position (offset + pitch / 2) as a candidate and picks
// whichever is closer. Many real theater maps stagger alternating rows/seats by half
// a seat's pitch (a brick-like pattern) for better sight lines, so this lets a single
// drag intelligently snap to either the main grid or the stagger pattern depending on
// where the user actually drops it, instead of always forcing one fixed alignment.
const snapToNearestPitchCandidate = (value: number, pitch: number, offset: number): number => {
	if (!(pitch > 0)) return value

	const snappedToGrid = Math.round((value - offset) / pitch) * pitch + offset
	const halfOffset = offset + pitch / 2
	const snappedToHalfGrid = Math.round((value - halfOffset) / pitch) * pitch + halfOffset

	return Math.abs(value - snappedToHalfGrid) < Math.abs(value - snappedToGrid) ? snappedToHalfGrid : snappedToGrid
}

// `offset` lets callers snap to a grid that's phase-shifted away from world (0, 0) -
// e.g. an imported map whose seats don't start at x/y 0. `pitch` defaults to the
// loaded map's detected row/seat spacing so snapping stays consistent even when a
// map doesn't use the editor's own default 44px pitch.
const snapToGridValue = (value: number, offset = 0, pitch = canvasGridPitch.value.y): number => {
	if (!snapToGrid.value) return value
	return snapToNearestPitchCandidate(value, pitch, offset)
}

const snapToHorizontalGridValue = (value: number, offset = 0, pitch = canvasGridPitch.value.x): number => {
	if (!snapToGrid.value) return value
	return snapToNearestPitchCandidate(value, pitch, offset)
}

// Row drags must stay rigid: snap the translation once and apply that same
// delta to every seat. Snapping each seat independently collapses custom
// spacing (rotated / hand-placed rows) onto the grid and causes overlaps.
const snapRowDragOffset = (deltaX: number, deltaY: number) => {
	return {
		x: snapToHorizontalGridValue(deltaX, 0),
		y: snapToGridValue(deltaY, 0)
	}
}

const getRowSeats = (rowIndex: number): Seat[] => {
	const row = rows.value[rowIndex]
	if (!Array.isArray(row?.seats)) {
		return []
	}
	return row.seats
}

const rowLayoutCache = computed<RowLayoutEntry[]>(() => {
	if (isCalculationPaused.value) {
		return []  // Skip calculation during slider drag
	}
	
	const layout: RowLayoutEntry[] = new Array(rows.value.length)

	for (let rowIndex = 0; rowIndex < rows.value.length; rowIndex++) {
		const row = rows.value[rowIndex]
		const seats = Array.isArray(row?.seats) ? row.seats : []
		const rowPositions: SeatPosition[] = new Array(seats.length)
		if (seats.length === 0) {
			layout[rowIndex] = {
				positions: rowPositions,
				bounds: null,
				centerX: 0,
				centerY: defaultSeatY(rowIndex),
				seatCount: 0
			}
			continue
		}

		let minOriginX = Number.POSITIVE_INFINITY
		let maxOriginX = Number.NEGATIVE_INFINITY
		let minOriginY = Number.POSITIVE_INFINITY
		let maxOriginY = Number.NEGATIVE_INFINITY

		for (let seatIndex = 0; seatIndex < seats.length; seatIndex++) {
			const seat = seats[seatIndex]
			if (!seat) {
				continue
			}

			const x = seat.x ?? defaultSeatX(seatIndex)
			const y = seat.y ?? defaultSeatY(rowIndex)
			if (x < minOriginX) {
				minOriginX = x
			}
			if (x > maxOriginX) {
				maxOriginX = x
			}
			if (y < minOriginY) {
				minOriginY = y
			}
			if (y > maxOriginY) {
				maxOriginY = y
			}
		}

		const centerX = Number.isFinite(minOriginX) && Number.isFinite(maxOriginX)
			? (minOriginX + maxOriginX) / 2
			: 0
		const centerY = Number.isFinite(minOriginY) && Number.isFinite(maxOriginY)
			? (minOriginY + maxOriginY) / 2
			: defaultSeatY(rowIndex)
		const seatCount = Math.max(1, seats.length)
		const rotation = Number(row?.rotation ?? 0)
		const curvature = Number(row?.curvature ?? 0)
		const skew = Number(row?.skew ?? 0)
		const radians = (rotation * Math.PI) / 180
		const cos = Math.cos(radians)
		const sin = Math.sin(radians)
		let minX = Number.POSITIVE_INFINITY
		let maxX = Number.NEGATIVE_INFINITY
		let minY = Number.POSITIVE_INFINITY
		let maxY = Number.NEGATIVE_INFINITY

		for (let seatIndex = 0; seatIndex < seats.length; seatIndex++) {
			const seat = seats[seatIndex]
			if (!seat) {
				continue
			}

			const seatOriginX = seat.x ?? defaultSeatX(seatIndex)
			const seatOriginY = seat.y ?? defaultSeatY(rowIndex)
			const normalizedSeatPosition = seatCount > 1 ? (seatIndex / (seatCount - 1)) * 2 - 1 : 0

			const localX = seatOriginX - centerX
			const localY = seatOriginY - centerY
			const curvedY = localY - curvature * (1 - normalizedSeatPosition * normalizedSeatPosition)
			const skewedX = localX + skew * normalizedSeatPosition

			const x = centerX + (skewedX * cos - curvedY * sin)
			const y = centerY + (skewedX * sin + curvedY * cos)
			rowPositions[seatIndex] = { x, y }

			if (x < minX) {
				minX = x
			}
			if (x > maxX) {
				maxX = x
			}
			if (y < minY) {
				minY = y
			}
			if (y > maxY) {
				maxY = y
			}
		}

		const bounds = Number.isFinite(minX) && Number.isFinite(maxX) && Number.isFinite(minY) && Number.isFinite(maxY)
			? {
				minX,
				maxX: maxX + seatSize,
				minY,
				maxY: maxY + seatSize
			}
			: null

		layout[rowIndex] = {
			positions: rowPositions,
			bounds,
			centerX,
			centerY,
			seatCount: seats.length
		}
	}
	return layout
})

const rowSeatPositions = computed<SeatPosition[][]>(() => rowLayoutCache.value.map((entry) => entry.positions))

const rowSeatBounds = computed<Array<RowSeatBounds | null>>(() => rowLayoutCache.value.map((entry) => entry.bounds))

const allRowsVisible = computed<VisibleRowEntry[]>(() => {
	const visible: VisibleRowEntry[] = []

	for (let rowIndex = 0; rowIndex < rows.value.length; rowIndex++) {
		const rowSeats = getRowSeats(rowIndex)
		if (rowSeats.length === 0) {
			continue
		}

		const seats: VisibleSeatEntry[] = []
		for (let seatIndex = 0; seatIndex < rowSeats.length; seatIndex++) {
			const seat = rowSeats[seatIndex]
			if (seat) {
				seats.push({ seat, seatIndex })
			}
		}

		if (seats.length > 0) {
			visible.push({ rowIndex, seats })
		}
	}

	return visible
})

const visibleRows = computed<VisibleRowEntry[]>(() => {
	if (isCalculationPaused.value) {
		return allRowsVisible.value  // Skip culling during drag
	}
	
	if (!shouldUseViewportCulling.value) {
		return allRowsVisible.value
	}


	const bounds = viewportWorldBounds.value
	const shouldCull = !!bounds
	const margin = shouldUseLiteSeatRendering.value ? DEFAULT_GRID_SIZE : VIEWPORT_CULLING_MARGIN
	const minX = bounds ? bounds.left - margin : -Infinity
	const maxX = bounds ? bounds.right + margin : Infinity
	const minY = bounds ? bounds.top - margin : -Infinity
	const maxY = bounds ? bounds.bottom + margin : Infinity
	const visible: VisibleRowEntry[] = []

	for (let rowIndex = 0; rowIndex < rows.value.length; rowIndex++) {
		const rowSeats = getRowSeats(rowIndex)
		if (rowSeats.length === 0) {
			continue
		}

		const seats: VisibleSeatEntry[] = []
		const rowPositions = rowSeatPositions.value[rowIndex] ?? []
		const forceVisible = selectedRowIndices.value.has(rowIndex) || isRowInActiveDrag(rowIndex)
		const rowBounds = rowSeatBounds.value[rowIndex]

		if (!shouldCull || forceVisible) {
			for (let seatIndex = 0; seatIndex < rowSeats.length; seatIndex++) {
				const seat = rowSeats[seatIndex]
				if (seat) {
					seats.push({ seat, seatIndex })
				}
			}

			if (seats.length > 0) {
				visible.push({ rowIndex, seats })
			}
			continue
		}

		if (rowBounds && (rowBounds.maxX < minX || rowBounds.minX > maxX || rowBounds.maxY < minY || rowBounds.minY > maxY)) {
			continue
		}

		if (rowBounds && rowBounds.minX >= minX && rowBounds.maxX <= maxX && rowBounds.minY >= minY && rowBounds.maxY <= maxY) {
			for (let seatIndex = 0; seatIndex < rowSeats.length; seatIndex++) {
				const seat = rowSeats[seatIndex]
				if (seat) {
					seats.push({ seat, seatIndex })
				}
			}

			if (seats.length > 0) {
				visible.push({ rowIndex, seats })
			}
			continue
		}

		for (let seatIndex = 0; seatIndex < rowSeats.length; seatIndex++) {
			const seat = rowSeats[seatIndex]
			if (!seat) {
				continue
			}

			const position = rowPositions[seatIndex]
			const x = position?.x ?? (seat.x ?? defaultSeatX(seatIndex))
			const y = position?.y ?? (seat.y ?? defaultSeatY(rowIndex))
			if (x + seatSize >= minX && x <= maxX && y + seatSize >= minY && y <= maxY) {
				seats.push({ seat, seatIndex })
			}
		}

		if (seats.length === 0) {
			continue
		}

		visible.push({ rowIndex, seats })
	}
	return visible
})

const isRowInActiveDrag = (rowIndex: number) => {
	const state = rowDragState.value
	if (!state) {
		return false
	}
	return state.rowIndexSet.has(rowIndex)
}

const getSeatById = (seatId: string) => {
	const [rowPart, seatPart] = seatId.split('-')
	const rowIndex = Number(rowPart)
	const seatIndex = Number(seatPart)
	if (!Number.isFinite(rowIndex) || !Number.isFinite(seatIndex)) {
		return null
	}
	const row = rows.value[rowIndex]
	if (!row || !Array.isArray(row.seats)) {
		return null
	}
	const seat = row.seats[seatIndex]
	if (!seat) {
		return null
	}
	return {
		rowIndex,
		seatIndex,
		seat
	}
}

const getSeatPosition = (rowIndex: number, seatIndex: number, seat: Seat) => {
	const cachedPosition = rowSeatPositions.value[rowIndex]?.[seatIndex]
	if (cachedPosition) {
		const rowState = rowDragState.value
		if (rowState && rowState.rowIndexSet.has(rowIndex)) {
			return {
				x: cachedPosition.x + rowDragPreviewOffset.value.x,
				y: cachedPosition.y + rowDragPreviewOffset.value.y
			}
		}

		return cachedPosition
	}

	const seatOriginX = seat.x ?? defaultSeatX(seatIndex)
	const seatOriginY = seat.y ?? defaultSeatY(rowIndex)
	const context = rowLayoutCache.value[rowIndex]
	const centerX = context?.centerX ?? seatOriginX
	const centerY = context?.centerY ?? seatOriginY
	const seatCount = context?.seatCount ?? 1

	const row = rows.value[rowIndex]
	const rotation = Number(row?.rotation ?? 0)
	const curvature = Number(row?.curvature ?? 0)
	const skew = Number(row?.skew ?? 0)
	const normalizedSeatPosition = seatCount > 1 ? (seatIndex / (seatCount - 1)) * 2 - 1 : 0

	const localX = seatOriginX - centerX
	const localY = seatOriginY - centerY
	const curvedY = localY - curvature * (1 - normalizedSeatPosition * normalizedSeatPosition)
	const skewedX = localX + skew * normalizedSeatPosition

	const radians = (rotation * Math.PI) / 180
	const cos = Math.cos(radians)
	const sin = Math.sin(radians)

	const transformedX = skewedX * cos - curvedY * sin
	const transformedY = skewedX * sin + curvedY * cos

	let x = centerX + transformedX
	let y = centerY + transformedY

	const rowState = rowDragState.value
	if (rowState && rowState.rowIndexSet.has(rowIndex)) {
		x += rowDragPreviewOffset.value.x
		y += rowDragPreviewOffset.value.y
	}

	return {
		x,
		y
	}
}

const selectedRowActionStyle = computed(() => {
	if (editMode.value !== 'seats' || seatEditMode.value !== 'rows' || selectedRowIndices.value.size === 0) {
		return null
	}

	let minX = Number.POSITIVE_INFINITY
	let maxX = Number.NEGATIVE_INFINITY
	let minY = Number.POSITIVE_INFINITY
	const dragState = rowDragState.value
	const dragOffsetX = rowDragPreviewOffset.value.x
	const dragOffsetY = rowDragPreviewOffset.value.y

	Array.from(selectedRowIndices.value).forEach((selectedIndex) => {
		const bounds = rowSeatBounds.value[selectedIndex]
		if (!bounds) {
			return
		}

		const isDragged = !!dragState?.rowIndexSet.has(selectedIndex)
		const offsetX = isDragged ? dragOffsetX : 0
		const offsetY = isDragged ? dragOffsetY : 0
		const rowMinX = bounds.minX + offsetX
		const rowMaxX = bounds.maxX + offsetX
		const rowMinY = bounds.minY + offsetY

		if (rowMinX < minX) {
			minX = rowMinX
		}
		if (rowMaxX > maxX) {
			maxX = rowMaxX
		}
		if (rowMinY < minY) {
			minY = rowMinY
		}
	})

	if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY)) {
		return null
	}

	const left = cameraX.value + minX * cameraScale.value
	const top = cameraY.value + minY * cameraScale.value - 16
	const width = Math.max(48, (maxX - minX) * cameraScale.value)

	return {
		left: `${left}px`,
		top: `${top}px`,
		width: `${width}px`
	}
})

const selectedSeatActionStyle = computed(() => {
	if (editMode.value !== 'seats' || seatEditMode.value !== 'seats' || selectedSeatIds.value.size === 0) {
		return null
	}

	let minX = Number.POSITIVE_INFINITY
	let maxX = Number.NEGATIVE_INFINITY
	let minY = Number.POSITIVE_INFINITY

	Array.from(selectedSeatIds.value).forEach((seatKey) => {
		const parsed = parseSeatKey(seatKey)
		if (!parsed) {
			return
		}
		const row = rows.value[parsed.rowIndex]
		const seat = Array.isArray(row?.seats) ? row.seats[parsed.seatIndex] : null
		if (!seat) {
			return
		}
		const position = getSeatPosition(parsed.rowIndex, parsed.seatIndex, seat)
		const seatWidth = seat.w ?? seatSize
		const seatHeight = seat.h ?? seatSize
		if (position.x < minX) {
			minX = position.x
		}
		if (position.x + seatWidth > maxX) {
			maxX = position.x + seatWidth
		}
		if (position.y < minY) {
			minY = position.y
		}
		void seatHeight
	})

	if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY)) {
		return null
	}

	const left = cameraX.value + minX * cameraScale.value
	const top = cameraY.value + minY * cameraScale.value - 16
	const width = Math.max(48, (maxX - minX) * cameraScale.value)

	return {
		left: `${left}px`,
		top: `${top}px`,
		width: `${width}px`
	}
})

const commitRowDragPreview = () => {
	const state = rowDragState.value
	if (!state) {
		return
	}

	const offsetX = rowDragPreviewOffset.value.x
	const offsetY = rowDragPreviewOffset.value.y

	state.rowIndices.forEach((rowIndex) => {
		const seats = getRowSeats(rowIndex)
		const originSeats = state.originSeatsByRow[rowIndex] ?? []

		seats.forEach((seat, idx) => {
			const origin = originSeats[idx]
			if (!origin) {
				return
			}

			seat.x = origin.x + offsetX
			seat.y = origin.y + offsetY
		})
	})

	rowDragState.value = null
	rowDragPreviewOffset.value = { x: 0, y: 0 }
}

const isRowSelected = (rowIndex: number) => {
	return selectedRowIndices.value.has(rowIndex)
}

const isRowDragging = (rowIndex: number) => {
	const state = rowDragState.value
	return !!state && state.rowIndexSet.has(rowIndex)
}

const isRowHighlighted = (rowIndex: number) => {
	return isRowSelected(rowIndex) || isRowDragging(rowIndex)
}

const getDraggedRowIndices = (rowIndex: number) => {
	if (selectedRowIndices.value.has(rowIndex) && selectedRowIndices.value.size > 1) {
		return Array.from(selectedRowIndices.value).sort((a, b) => a - b)
	}
	return [rowIndex]
}

const onRowLabelClick = (rowIndex: number, event: any) => {
	if (editMode.value !== 'seats' || seatEditMode.value !== 'rows') {
		return
	}

	activeRowIndex.value = rowIndex
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	selectedLabelId.value = null
	selectedShapeId.value = null
	if (activeSidebar.value === 'label' || activeSidebar.value === 'shape') {
		activeSidebar.value = null
	}
	const nativeEvt = event?.evt
	const shiftSelect = !!nativeEvt?.shiftKey
	const additiveSelect = !!(nativeEvt?.ctrlKey || nativeEvt?.metaKey)

	if (shiftSelect && rows.value.length > 0) {
		const anchor = lastSelectedRowIndex.value ?? activeRowIndex.value ?? rowIndex
		const start = Math.max(0, Math.min(anchor, rowIndex))
		const end = Math.min(rows.value.length - 1, Math.max(anchor, rowIndex))
		const nextSelection = additiveSelect ? new Set(selectedRowIndices.value) : new Set<number>()
		for (let idx = start; idx <= end; idx++) {
			nextSelection.add(idx)
		}
		selectedRowIndices.value = nextSelection
		lastSelectedRowIndex.value = rowIndex
		return
	}

	if (additiveSelect) {
		const nextSelection = new Set(selectedRowIndices.value)
		if (nextSelection.has(rowIndex)) {
			nextSelection.delete(rowIndex)
		} else {
			nextSelection.add(rowIndex)
		}
		selectedRowIndices.value = nextSelection
	} else {
		selectedRowIndices.value = new Set([rowIndex])
	}
	lastSelectedRowIndex.value = rowIndex
}

const onSeatClick = (rowIndex: number, seatIndex: number, event: any) => {
	if (editMode.value !== 'seats') {
		return
	}

	if (seatEditMode.value === 'rows') {
		onRowLabelClick(rowIndex, event)
		return
	}

	stopKonvaEvent(event)

	activeRowIndex.value = rowIndex
	selectedLabelId.value = null
	selectedShapeId.value = null
	selectedWallIds.value = new Set()
	if (activeSidebar.value === 'label' || activeSidebar.value === 'shape' || activeSidebar.value === 'row') {
		activeSidebar.value = null
	}

	const seatKey = toSeatKey(rowIndex, seatIndex)
	const nativeEvt = event?.evt
	const shiftSelect = !!nativeEvt?.shiftKey
	const additiveSelect = !!(nativeEvt?.ctrlKey || nativeEvt?.metaKey)

	if (shiftSelect && lastSelectedSeatId.value) {
		const anchorSeat = parseSeatKey(lastSelectedSeatId.value)
		if (anchorSeat && anchorSeat.rowIndex === rowIndex) {
			const start = Math.min(anchorSeat.seatIndex, seatIndex)
			const end = Math.max(anchorSeat.seatIndex, seatIndex)
			const nextSelection = additiveSelect ? new Set(selectedSeatIds.value) : new Set<string>()
			for (let idx = start; idx <= end; idx++) {
				nextSelection.add(toSeatKey(rowIndex, idx))
			}
			selectedSeatIds.value = nextSelection
			selectedRowIndices.value = new Set()
			lastSelectedRowIndex.value = null
			lastSelectedSeatId.value = seatKey
			activeSidebar.value = nextSelection.size > 0 ? 'seat' : null
			return
		}
	}

	if (additiveSelect) {
		const nextSelection = new Set(selectedSeatIds.value)
		if (nextSelection.has(seatKey)) {
			nextSelection.delete(seatKey)
		} else {
			nextSelection.add(seatKey)
			lastSelectedSeatId.value = seatKey
		}
		selectedSeatIds.value = nextSelection
		selectedRowIndices.value = new Set()
		lastSelectedRowIndex.value = null
		activeSidebar.value = nextSelection.size > 0 ? 'seat' : null
		return
	}

	selectedSeatIds.value = new Set([seatKey])
	lastSelectedSeatId.value = seatKey
	selectedRowIndices.value = new Set()
	lastSelectedRowIndex.value = null
	activeSidebar.value = 'seat'
}

const startRowDrag = (rowIndex: number, startX: number, startY: number) => {
	const rowIndices = getDraggedRowIndices(rowIndex)
	const originSeatsByRow: Record<number, Array<{ x: number; y: number }>> = {}
	rowIndices.forEach((idx) => {
		const seats = getRowSeats(idx)
		originSeatsByRow[idx] = seats.map((seat, seatIndex) => ({
			x: seat.x ?? defaultSeatX(seatIndex),
			y: seat.y ?? defaultSeatY(idx)
		}))
	})

	rowDragState.value = {
		rowIndices,
		rowIndexSet: new Set(rowIndices),
		startX,
		startY,
		originSeatsByRow
	}
	rowDragPreviewOffset.value = { x: 0, y: 0 }
}

const applyRowDelta = (deltaX: number, deltaY: number) => {
	const state = rowDragState.value
	if (!state) {
		return
	}

	rowDragPreviewOffset.value = snapRowDragOffset(deltaX, deltaY)
}

const finalizeRowDrag = () => {
	const state = rowDragState.value
	if (!state) {
		return
	}
	commitRowDragPreview()
	rowDragState.value = null
	rowDragPreviewOffset.value = { x: 0, y: 0 }
	updateSeatmap()
}

const getRowAnchor = (rowIndex: number) => {
	const row = rows.value[rowIndex]
	const seats = getRowSeats(rowIndex)
	const labelAtEnd = getRowLabelPosition(row) === 'end'
	const fallbackY = defaultSeatY(rowIndex)
	const fallbackMargin = getSeatMargin()
	const fallbackLabelY = fallbackY + fallbackMargin.y + seatSize / 2 - 8

	if (seats.length === 0) {
		const labelOffsetX = labelAtEnd ? seatSize + 8 : -36
		return {
			x: 0,
			y: fallbackY,
			labelX: labelOffsetX,
			labelY: fallbackLabelY,
			labelOffsetX
		}
	}

	const seatIndex = labelAtEnd ? seats.length - 1 : 0
	const seat = seats[seatIndex]
	if (!seat) {
		const labelOffsetX = labelAtEnd ? seatSize + 8 : -36
		return {
			x: defaultSeatX(seatIndex),
			y: fallbackY,
			labelX: defaultSeatX(seatIndex) + labelOffsetX,
			labelY: fallbackLabelY,
			labelOffsetX
		}
	}

	const position = getSeatPosition(rowIndex, seatIndex, seat)
	const margin = getSeatMargin(seat)
	const seatWidth = seat.w ?? seatSize
	const seatHeight = seat.h ?? seatSize
	const labelOffsetX = labelAtEnd ? seatWidth + 8 : -36
	return {
		x: position.x,
		y: position.y,
		labelX: position.x + labelOffsetX,
		labelY: position.y + margin.y + seatHeight / 2 - 8,
		labelOffsetX
	}
}

const onRowLabelDragStart = (rowIndex: number, node: any) => {
	if (editMode.value !== 'seats' || seatEditMode.value !== 'rows') {
		return
	}
	activeRowIndex.value = rowIndex
	if (!selectedRowIndices.value.has(rowIndex)) {
		selectedRowIndices.value = new Set([rowIndex])
		lastSelectedRowIndex.value = rowIndex
	}
	const anchor = getRowAnchor(rowIndex)
	startRowDrag(rowIndex, anchor.x, anchor.y)
	node.position({ x: anchor.labelX, y: anchor.labelY })
}

const onRowLabelDragMove = (rowIndex: number, node: any) => {
	if (editMode.value !== 'seats' || seatEditMode.value !== 'rows') {
		return
	}
	const state = rowDragState.value
	if (!state || !state.rowIndexSet.has(rowIndex)) {
		return
	}

	const anchor = getRowAnchor(rowIndex)
	const labelOffsetY = anchor.labelY - anchor.y
	const desiredAnchorX = node.x() - anchor.labelOffsetX
	const desiredAnchorY = node.y() - labelOffsetY
	const deltaX = desiredAnchorX - state.startX
	const deltaY = desiredAnchorY - state.startY

	applyRowDelta(deltaX, deltaY)

	const updatedAnchor = getRowAnchor(rowIndex)
	node.position({ x: updatedAnchor.labelX, y: updatedAnchor.labelY })
}

const onRowLabelDragEnd = (rowIndex: number, node: any) => {
	if (editMode.value !== 'seats' || seatEditMode.value !== 'rows') {
		return
	}
	finalizeRowDrag()
	const anchor = getRowAnchor(rowIndex)
	node.position({ x: anchor.labelX, y: anchor.labelY })
}

const getRowLabelConfig = (rowIndex: number) => {
	const row = rows.value[rowIndex]
	const anchor = getRowAnchor(rowIndex)
	const rowHighlighted = isRowHighlighted(rowIndex)
	
	return {
		x: anchor.labelX,
		y: anchor.labelY,
		text: getRowDisplayLabel(row, rowIndex),
		fontSize: 14,
		fontFamily: 'Arial',
		fontStyle: 'bold',
		fill: rowHighlighted ? '#1d4ed8' : '#1e293b',
		verticalAlign: 'middle',
		listening: editMode.value === 'seats' && seatEditMode.value === 'rows',
		draggable: editMode.value === 'seats' && seatEditMode.value === 'rows',
		perfectDrawEnabled: false
	}
}

const stopKonvaEvent = (event: any) => {
	event?.evt?.preventDefault?.()
	if (event?.evt) {
		event.evt.cancelBubble = true
	}
	event?.cancelBubble && (event.cancelBubble = true)
}

const onSeatDragStart = (rowIndex: number, seatIndex: number, node: any) => {
	if (editMode.value !== 'seats' || seatEditMode.value !== 'rows') {
		return
	}

	activeRowIndex.value = rowIndex
	selectedSeatIds.value = new Set()
	lastSelectedSeatId.value = null
	if (!selectedRowIndices.value.has(rowIndex)) {
		selectedRowIndices.value = new Set([rowIndex])
		lastSelectedRowIndex.value = rowIndex
	}

	const row = rows.value[rowIndex]
	const seat = Array.isArray(row?.seats) ? row.seats[seatIndex] : null
	const position = seat ? getSeatPosition(rowIndex, seatIndex, seat) : {
		x: defaultSeatX(seatIndex),
		y: defaultSeatY(rowIndex)
	}

	startRowDrag(rowIndex, position.x, position.y)
	const margin = getSeatMargin(seat ?? undefined)
	node.position({ x: position.x + margin.x, y: position.y + margin.y })
}

const onSeatDragMove = (rowIndex: number, seatIndex: number, node: any) => {
	if (editMode.value !== 'seats' || seatEditMode.value !== 'rows') {
		return
	}

	const state = rowDragState.value
	if (!state || !state.rowIndexSet.has(rowIndex)) {
		return
	}

	const row = rows.value[rowIndex]
	const seat = Array.isArray(row?.seats) ? row.seats[seatIndex] : null
	const margin = getSeatMargin(seat ?? undefined)
	const deltaX = (node.x() - margin.x) - state.startX
	const deltaY = (node.y() - margin.y) - state.startY
	applyRowDelta(deltaX, deltaY)

	if (seat) {
		const position = getSeatPosition(rowIndex, seatIndex, seat)
		node.position({ x: position.x + margin.x, y: position.y + margin.y })
	}
}

const onSeatDragEnd = (rowIndex: number, seatIndex: number, node: any) => {
	if (editMode.value !== 'seats' || seatEditMode.value !== 'rows') {
		return
	}

	const row = rows.value[rowIndex]
	const seat = Array.isArray(row?.seats) ? row.seats[seatIndex] : null
	if (seat) {
		const position = getSeatPosition(rowIndex, seatIndex, seat)
		const margin = getSeatMargin(seat)
		node.position({ x: position.x + margin.x, y: position.y + margin.y })
	}

	finalizeRowDrag()
}

const buildJohkuExportData = (): JohkuSeatmapExport => {
	// Ensure layout cache is live so baked seat positions include curvature/rotation/skew
	// even if the user hits save while a row-property slider still has calculation paused.
	const wasCalculationPaused = isCalculationPaused.value
	isCalculationPaused.value = false

	const allSeats: Array<Seat & { rowIndex: number; seatIndex: number; rowLabel: string }> = []
	rows.value.forEach((row, rowIndex) => {
		if (!Array.isArray(row.seats)) {
			return
		}
		const rowLabel = getRowDisplayLabel(row, rowIndex)
		row.seats.forEach((seat, seatIndex) => {
			allSeats.push({
				...seat,
				rowIndex,
				seatIndex,
				rowLabel
			})
		})
	})

	// `seat` is left `null` when there's no real Johku seat id yet (a seat drawn/added in
	// this session). The backend is responsible for creating the DB row and assigning the
	// real id on save - the editor must never invent a fake sequential id here, otherwise
	// the backend can't tell new seats apart from existing ones.
	// Flat `seats[].x/y` are baked from getSeatPosition (curvature/rotation/skew applied) so
	// production SeatMap viewers show arcs correctly. Parametric `rows[]` below still stores
	// base origins + transforms for non-destructive re-editing.
	const exportedSeats = allSeats.map((seat) => {
		const position = getSeatPosition(seat.rowIndex, seat.seatIndex, seat)
		return {
			x: Math.round(position.x),
			y: Math.round(position.y),
			// Preserve the imported seat's own box size/corner-radius so re-saving an
			// existing seat map doesn't silently resize it back to the editor's default.
			w: seat.w ?? DEFAULT_SEAT_SIZE,
			h: seat.h ?? DEFAULT_SEAT_SIZE,
			r: seat.r ?? DEFAULT_SEAT_CORNER_RADIUS,
			type: 'rrect' as const,
			seat: seat.seatId ?? null,
			header: seat.rowLabel,
			title: (seat.title && String(seat.title).trim() !== '') ? String(seat.title).trim() : (seat.index ?? seat.seatIndex),
			footer: null,
			disabled: !!seat.disabled,
			zone: seat.zone,
			bg: seat.bg,
			linkedSeats: seat.linkedSeats
		}
	})

	const exportedWalls = walls.value.map((wall) => ({
		type: 'line',
		subtype: 'wall',
		points: [
			Math.round(wall.x1),
			Math.round(wall.y1),
			Math.round(wall.x2),
			Math.round(wall.y2)
		],
		stroke: wall.stroke ?? '#334155',
		strokeWidth: wall.strokeWidth ?? 6,
		lineCap: 'round',
		lineJoin: 'round'
	}))

	// Strip the editor-only `id` bookkeeping field and write back the plain shape shape
	// the production seat map viewer expects (SeatMap.vue -> makeRRect).
	const exportedDecorativeShapes: ImportShape[] = decorativeShapes.value.map((shape) => {
		const exported: ImportShape = {
			type: shape.type,
			x: Math.round(shape.x),
			y: Math.round(shape.y),
			w: Math.round(shape.w),
			h: Math.round(shape.h),
			r: Math.round(shape.r ?? 0),
			fill: shape.fill
		}
		if (shape.border) {
			exported.border = shape.border
		}
		if (shape.text) {
			exported.text = shape.text
		}
		if (shape.subtype) {
			exported.subtype = shape.subtype
		}
		return exported
	})

	// Tight canvas: normalize so content sits with a fixed margin on all sides.
	// Do not preserve a larger imported canvas — that left large empty margins in production viewers.
	const bounds = getSeatmapContentBounds()
	const canvasPadding = 40
	const offsetX = bounds ? canvasPadding - bounds.minX : 0
	const offsetY = bounds ? canvasPadding - bounds.minY : 0
	const canvas = {
		w: Math.round(bounds ? (bounds.maxX - bounds.minX + canvasPadding * 2) : 0),
		h: Math.round(bounds ? (bounds.maxY - bounds.minY + canvasPadding * 2) : 0)
	}

	const roundOffset = (value: number, offset: number) => Math.round(value + offset)

	const normalizedSeats = exportedSeats.map((seat) => ({
		...seat,
		x: roundOffset(seat.x, offsetX),
		y: roundOffset(seat.y, offsetY)
	}))

	const normalizedWalls = exportedWalls.map((wall) => ({
		...wall,
		points: [
			roundOffset(wall.points[0], offsetX),
			roundOffset(wall.points[1], offsetY),
			roundOffset(wall.points[2], offsetX),
			roundOffset(wall.points[3], offsetY)
		]
	}))

	const normalizedDecorativeShapes = exportedDecorativeShapes.map((shape) => ({
		...shape,
		x: typeof shape.x === 'number' ? roundOffset(shape.x, offsetX) : shape.x,
		y: typeof shape.y === 'number' ? roundOffset(shape.y, offsetY) : shape.y
	}))

	const normalizedUnsupportedShapes: ImportShape[] = unsupportedShapes.value.map((shape) => {
		const normalized: ImportShape = { ...shape }
		if (typeof normalized.x === 'number') {
			normalized.x = roundOffset(normalized.x, offsetX)
		}
		if (typeof normalized.y === 'number') {
			normalized.y = roundOffset(normalized.y, offsetY)
		}
		if (Array.isArray(normalized.points) && normalized.points.length >= 4) {
			normalized.points = normalized.points.map((point, index) => (
				typeof point === 'number'
					? roundOffset(point, index % 2 === 0 ? offsetX : offsetY)
					: point
			))
		}
		return normalized
	})

	const normalizedLabels = labels.value.map((label) => ({
		...label,
		x: roundOffset(label.x, offsetX),
		y: roundOffset(label.y, offsetY)
	}))

	const exportData: JohkuSeatmapExport = {
		canvas,
		seats: normalizedSeats,
		rows: rows.value.map((row, rowIndex) => ({
			label: getRowDisplayLabel(row, rowIndex),
			rotation: Number(row.rotation ?? 0),
			curvature: Number(row.curvature ?? 0),
			skew: Number(row.skew ?? 0),
			startNumber: Number(row.startNumber ?? 1),
			reverseNumbering: !!row.reverseNumbering,
			labelPosition: getRowLabelPosition(row),
			seats: Array.isArray(row.seats)
				? row.seats.map((seat, seatIndex) => ({
					index: seat.index ?? seatIndex + 1,
					title: seat.title,
					// Offset base origins by the same canvas normalization so editor
					// reopen stays aligned with baked seats / labels / walls.
					x: roundOffset(seat.x ?? defaultSeatX(seatIndex), offsetX),
					y: roundOffset(seat.y ?? defaultSeatY(rowIndex), offsetY),
					seatId: seat.seatId,
					disabled: !!seat.disabled,
					zone: seat.zone,
					bg: seat.bg,
					linkedSeats: seat.linkedSeats
				}))
				: []
		})),
		labels: normalizedLabels,
		// Editable decorative shapes first, then any legacy shape kind we don't
		// understand (kept untouched aside from canvas normalization), then walls.
		shapes: [...normalizedDecorativeShapes, ...normalizedUnsupportedShapes, ...normalizedWalls]
	}

	isCalculationPaused.value = wasCalculationPaused
	return exportData
}

const exportSeatmap = () => {
	const seatmapData = buildJohkuExportData()
	const json = JSON.stringify(seatmapData, null, 2)
	const blob = new Blob([json], { type: 'application/json' })
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = `seatmap-${Date.now()}.json`
	a.click()
	URL.revokeObjectURL(url)
}

const handleSave = () => {
	emit('save', buildJohkuExportData())
}

const handleClose = () => {
	emit('close')
}

onMounted(async () => {
	if (props.initialData) {
		try {
			hydrateFromImportedData(props.initialData as ImportSeatMap)
		} catch (error) {
			console.error('Failed to load existing seat map, starting from a blank one', error)
			initializeDefaultSeatmap()
		}
		resetHistory()
		await focusImportedSeatmap()
	} else {
		initializeDefaultSeatmap()
	}

	await loadSeatmapZones()

	if (typeof window !== 'undefined') {
		const storedGuideSetting = window.localStorage.getItem(guideStorageKey)
		showGuideOnStartup.value = storedGuideSetting !== 'hidden'
		showGuide.value = showGuideOnStartup.value
	}
	window.addEventListener('keydown', onEditorKeyDown)
})

onBeforeUnmount(() => {
	if (wallHistoryCommitTimer !== null) {
		clearTimeout(wallHistoryCommitTimer)
		wallHistoryCommitTimer = null
	}
	if (historyCommitTimer !== null) {
		clearTimeout(historyCommitTimer)
		historyCommitTimer = null
	}
	if (rowPropertyUpdateTimer !== null) {
		clearTimeout(rowPropertyUpdateTimer)
		rowPropertyUpdateTimer = null
	}
	if (sliderEndTimer !== null) {
		clearTimeout(sliderEndTimer)
		sliderEndTimer = null
	}
	window.removeEventListener('keydown', onEditorKeyDown)
})
</script>

<style scoped src="./SeatmapEditor.css"></style>